import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Send, Users as UsersIcon, Calendar, MessageSquare, Image as ImageIcon, Bell, X, Edit2, Video, Settings, Upload, Heart, CheckCircle, Clock, Archive, BarChart3, UserCog, Shield, Eye, EyeOff, TrendingUp, Activity, FileText, Book, Download } from 'lucide-react';
import { API } from '../App';
import SMSDashboard from '../components/SMSDashboard';
import { useSiteSettings } from '../context/SiteSettingsContext';
import ImagePicker from '../components/ImagePicker';
import html2pdf from 'html2pdf.js';

// Helper to parse date string without timezone shift
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

// Helper to format time to 12-hour AM/PM format
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
    return timeStr;
  }
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeStr;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const { refreshSettings, settings, getFullAddress } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [media, setMedia] = useState([]);
  const [smsGroups, setSmsGroups] = useState([]);
  const [smsMessages, setSmsMessages] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [prayerFilter, setPrayerFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // User Management State
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    notification_preferences: {
      announcements_email: false,
      announcements_text: false,
      event_reminders_email: false,
      event_reminders_text: false
    }
  });
  const [showLoginHistory, setShowLoginHistory] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [savingUser, setSavingUser] = useState(false);

  // Analytics State
  const [adminStats, setAdminStats] = useState(null);

  // Prayer share modal state
  const [shareModal, setShareModal] = useState({ show: false, request: null });
  const [shareForm, setShareForm] = useState({
    recipient_type: '',
    group_ids: [],
    custom_message: ''
  });
  const [sharing, setSharing] = useState(false);

  // Form states
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    image_url: '',
    is_recurring: false,
    recurrence_pattern: '',
    is_featured: false
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventViewMode, setEventViewMode] = useState('upcoming'); // 'upcoming', 'all', 'month'
  const [selectedMonth, setSelectedMonth] = useState('');

  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    priority: 'normal',
    image_url: ''
  });
  const [uploadingAnnouncementImage, setUploadingAnnouncementImage] = useState(false);

  const [mediaForm, setMediaForm] = useState({
    type: 'image',
    url: '',
    title: '',
    description: ''
  });
  const [uploadingMediaImage, setUploadingMediaImage] = useState(false);

  // Site Settings State
  const [siteSettings, setSiteSettings] = useState({
    church_name: '',
    address: '',
    city_state_zip: '',
    phone: '',
    email: '',
    sunday_school_time: '',
    sunday_service_time: '',
    mission_statement: '',
    welcome_text: '',
    logo_url: '',
    hero_image_url: '',
    pastor_name: '',
    pastor_title: '',
    pastor_bio: '',
    pastor_image_url: '',
    footer_text: '',
    visit_us_image_url: '',
    contact_page_image_url: '',
    admin_emails: []
  });
  const [uploadingSiteImage, setUploadingSiteImage] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSubTab, setSettingsSubTab] = useState('general'); // general, homepage, about, footer

  const [smsGroupForm, setSmsGroupForm] = useState({
    name: '',
    description: ''
  });

  const [memberForm, setMemberForm] = useState({
    phone: '',
    name: '',
    group_id: ''
  });

  const [smsForm, setSmsForm] = useState({
    group_id: '',
    message: ''
  });

  useEffect(() => {
    // Check both user prop and localStorage for admin status
    const storedUser = localStorage.getItem('user');
    let isAdmin = false;
    
    if (user && user.is_admin) {
      isAdmin = true;
    } else if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        isAdmin = parsedUser.is_admin;
      } catch (e) {
        console.error('Failed to parse stored user');
      }
    }
    
    if (!isAdmin) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchData();
    checkVonageBalance();
  }, [user, navigate]);

  // Vonage low-balance alert (shows a popup when balance falls to <= $1.00)
  const [lowBalanceAlert, setLowBalanceAlert] = useState(null);

  const checkVonageBalance = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/sms/account-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const balance = res.data?.balance;
      // Only alert once per browser session per balance threshold
      const dismissedAt = sessionStorage.getItem('vonage_low_balance_dismissed_at');
      if (balance !== null && balance !== undefined && balance <= 1 && !dismissedAt) {
        setLowBalanceAlert({
          balance,
          configured: res.data?.configured,
          warning: res.data?.warning
        });
      }
    } catch (e) {
      // Non-fatal — admin can still use dashboard without balance check
    }
  };

  const dismissLowBalanceAlert = () => {
    sessionStorage.setItem('vonage_low_balance_dismissed_at', String(Date.now()));
    setLowBalanceAlert(null);
  };

  // Update event form default location when settings change
  useEffect(() => {
    if (settings.address && !editingEvent && eventForm.title === '') {
      const defaultLocation = settings.address + (settings.city_state_zip ? `, ${settings.city_state_zip}` : '');
      setEventForm(prev => ({ ...prev, location: defaultLocation }));
    }
  }, [settings.address, settings.city_state_zip]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      // Load essential data first to show dashboard quickly
      const settingsRes = await axios.get(`${API}/site-settings`);
      setSiteSettings(settingsRes.data);
      setLoading(false); // Show dashboard immediately

      // Load remaining data in background
      const [eventsRes, announcementsRes, mediaRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/announcements`),
        axios.get(`${API}/media`)
      ]);

      setEvents(eventsRes.data);
      setAnnouncements(announcementsRes.data);
      setMedia(mediaRes.data);

      // Fetch SMS data, prayer requests, users, and stats separately with auth
      try {
        const [groupsRes, messagesRes, prayerRes, usersRes, statsRes] = await Promise.all([
          axios.get(`${API}/sms/groups`, config),
          axios.get(`${API}/sms/messages`, config),
          axios.get(`${API}/prayer-requests`, config),
          axios.get(`${API}/users`, config),
          axios.get(`${API}/admin/stats`, config)
        ]);
        setSmsGroups(groupsRes.data);
        setSmsMessages(messagesRes.data);
        setPrayerRequests(prayerRes.data);
        setAllUsers(usersRes.data);
        setAdminStats(statsRes.data);
      } catch (authError) {
        console.error('Auth data fetch error:', authError);
        // Continue without auth data if auth fails
      }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setEventForm({ ...eventForm, image_url: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log out and log back in.');
      } else {
        toast.error('Failed to upload image');
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleMediaImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingMediaImage(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setMediaForm({ ...mediaForm, url: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingMediaImage(false);
    }
  };

  const handleSiteImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingSiteImage(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSiteSettings({ ...siteSettings, [field]: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingSiteImage(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.put(`${API}/site-settings`, siteSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site settings saved successfully!');
      console.log('Settings saved:', response.data);
      // Refresh settings globally so all pages update
      refreshSettings();
    } catch (error) {
      console.error('Save settings error:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save settings');
      }
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSharePrayerRequest = async () => {
    if (!shareForm.recipient_type) {
      toast.error('Please select who to share with');
      return;
    }
    if (shareForm.recipient_type === 'sms_groups' && shareForm.group_ids.length === 0) {
      toast.error('Please select at least one SMS group');
      return;
    }

    setSharing(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/prayer-requests/${shareModal.request.id}/share`,
        shareForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShareModal({ show: false, request: null });
      setShareForm({ recipient_type: '', group_ids: [], custom_message: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share prayer request');
    } finally {
      setSharing(false);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event.id);
    setEventForm({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      location: event.location,
      image_url: event.image_url || '',
      is_recurring: event.is_recurring,
      recurrence_pattern: event.recurrence_pattern || '',
      is_featured: event.is_featured || false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper to get default event location from site settings
  const getDefaultEventLocation = () => {
    if (settings.address && settings.city_state_zip) {
      return `${settings.address}, ${settings.city_state_zip}`;
    }
    return settings.address || '';
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      description: '',
      date: '',
      time: '',
      location: getDefaultEventLocation(),
      image_url: '',
      is_recurring: false,
      recurrence_pattern: '',
      is_featured: false
    });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      if (editingEvent) {
        // Update existing event
        await axios.put(`${API}/events/${editingEvent}`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event updated successfully!');
        setEditingEvent(null);
      } else {
        // Create new event
        await axios.post(`${API}/events`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created successfully!');
      }
      
      setEventForm({
        title: '',
        description: '',
        date: '',
        time: '',
        location: getDefaultEventLocation(),
        image_url: '',
        is_recurring: false,
        recurrence_pattern: '',
        is_featured: false
      });
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log out and log back in.');
      } else {
        toast.error(editingEvent ? 'Failed to update event' : 'Failed to create event');
      }
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleAnnouncementImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAnnouncementImage(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setAnnouncementForm({ ...announcementForm, image_url: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingAnnouncementImage(false);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/announcements`, announcementForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement created!');
      setAnnouncementForm({ title: '', content: '', priority: 'normal', image_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleCreateMedia = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/media`, mediaForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Media added!');
      setMediaForm({ type: 'image', url: '', title: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add media');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Media deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const handleCreateSmsGroup = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/sms/groups`, smsGroupForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SMS group created!');
      setSmsGroupForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleDeleteSmsGroup = async (groupId) => {
    if (!window.confirm('Are you sure?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/sms/groups/${memberForm.group_id}/members`, 
        { phone: memberForm.phone, name: memberForm.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Member added!');
      setMemberForm({ phone: '', name: '', group_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add member');
    }
  };

  const handleRemoveMember = async (groupId, phone) => {
    if (!window.confirm('Remove this member?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/sms/groups/${groupId}/members/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleSendSms = async (e) => {
    e.preventDefault();
    if (!window.confirm('Send this message to all group members?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/sms/send`, smsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Message sent to ${response.data.sent_count} recipients!`);
      setSmsForm({ group_id: '', message: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // User Management Functions
  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    const defaultPrefs = {
      announcements_email: false,
      announcements_text: false,
      event_reminders_email: false,
      event_reminders_text: false
    };
    setUserEditForm({
      first_name: userToEdit.first_name || '',
      last_name: userToEdit.last_name || '',
      phone: userToEdit.phone || '',
      notification_preferences: { ...defaultPrefs, ...(userToEdit.notification_preferences || {}) }
    });
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(`${API}/users/${selectedUser.id}`, userEditForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully!');
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    const token = localStorage.getItem('token');

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    const token = localStorage.getItem('token');

    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User role changed to ${newRole}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change user role');
    }
  };

  const handleViewLoginHistory = async (userId) => {
    const token = localStorage.getItem('token');

    try {
      const response = await axios.get(`${API}/users/${userId}/login-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoginHistory(response.data.login_history || []);
      setShowLoginHistory(userId);
    } catch (error) {
      toast.error('Failed to load login history');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-2xl" style={{ color: '#1e3a8a' }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#fffbeb' }}>
      {/* Vonage Low-Balance Alert Modal */}
      {lowBalanceAlert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          data-testid="vonage-low-balance-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border-4" style={{ borderColor: '#dc2626' }}>
            <div className="flex items-start gap-3 mb-4">
              <div
                className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: '#fee2e2' }}
              >
                <Bell size={22} color="#dc2626" />
              </div>
              <div>
                <h3
                  className="text-xl font-bold mb-1"
                  style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}
                  data-testid="low-balance-modal-title"
                >
                  Vonage Account Needs Top-Up
                </h3>
                <p className="text-sm text-slate-600">SMS sending may stop working soon</p>
              </div>
            </div>

            <div
              className="rounded-lg p-4 mb-4"
              style={{ background: '#fef2f2', border: '2px solid #fecaca' }}
            >
              <p className="text-base text-slate-800 mb-2" data-testid="low-balance-modal-message">
                Your Vonage SMS account balance has dropped to{' '}
                <span className="font-bold" style={{ color: '#dc2626' }}>
                  ${lowBalanceAlert.balance.toFixed(4)}
                </span>
                .
              </p>
              <p className="text-base font-semibold text-slate-800">
                Please top up your Vonage account with at least{' '}
                <span style={{ color: '#dc2626' }}>$10.00</span> to keep SMS messages flowing to your
                congregation.
              </p>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              When the balance falls below or reaches <strong>$1.00</strong>, Vonage may begin
              rejecting outgoing texts with a "Quota Exceeded" error and messages will not be
              delivered.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://dashboard.nexmo.com/billing-and-payments"
                target="_blank"
                rel="noreferrer"
                className="flex-1 px-5 py-3 rounded-lg font-bold text-center text-white transition hover:opacity-90"
                style={{ background: '#dc2626' }}
                data-testid="low-balance-topup-btn"
                onClick={dismissLowBalanceAlert}
              >
                Top Up Vonage Now →
              </a>
              <button
                onClick={dismissLowBalanceAlert}
                className="px-5 py-3 rounded-lg font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition"
                data-testid="low-balance-dismiss-btn"
              >
                Remind me later
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-5xl md:text-6xl font-bold mb-8 text-center" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }} data-testid="admin-dashboard-title">
          Admin Dashboard
        </h1>

        {/* Main Navigation - Always visible */}
        {activeTab !== 'dashboard' && (
          <div className="mb-6 text-center">
            <button
              onClick={() => setActiveTab('dashboard')}
              className="px-6 py-2 bg-slate-200 hover:bg-slate-300 rounded-full font-semibold text-slate-700 transition"
            >
              ← Back to Dashboard
            </button>
          </div>
        )}

        {/* Dashboard Home - Card Grid */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Page Content Management */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#1e3a8a' }}>
                📄 Edit Page Content
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                <button
                  onClick={() => setActiveTab('edit-homepage')}
                  className="card p-6 text-left hover:shadow-xl transition-all hover:scale-105"
                >
                  <div className="text-4xl mb-3">🏠</div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Homepage</h3>
                  <p className="text-slate-600">Edit hero banner, service times, mission statement</p>
                </button>
                <button
                  onClick={() => setActiveTab('edit-about')}
                  className="card p-6 text-left hover:shadow-xl transition-all hover:scale-105"
                >
                  <div className="text-4xl mb-3">ℹ️</div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>About Page</h3>
                  <p className="text-slate-600">Edit foundation content, pastor info, visit us section</p>
                </button>
                <button
                  onClick={() => setActiveTab('edit-contact')}
                  className="card p-6 text-left hover:shadow-xl transition-all hover:scale-105"
                >
                  <div className="text-4xl mb-3">📞</div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Contact Page</h3>
                  <p className="text-slate-600">Edit the contact page image</p>
                </button>
                <button
                  onClick={() => setActiveTab('edit-general')}
                  className="card p-6 text-left hover:shadow-xl transition-all hover:scale-105"
                >
                  <div className="text-4xl mb-3">⚙️</div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>General Info</h3>
                  <p className="text-slate-600">Church name, address, phone, logo - all pages</p>
                </button>
              </div>
            </div>

            {/* Content Management */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#1e3a8a' }}>
                📝 Manage Content
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                <button
                  onClick={() => setActiveTab('events')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <Calendar size={48} className="mx-auto mb-3 text-blue-900" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Events</h3>
                  <p className="text-slate-600 text-sm">{events.length} events</p>
                </button>
                <button
                  onClick={() => setActiveTab('announcements')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <Bell size={48} className="mx-auto mb-3 text-amber-600" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Announcements</h3>
                  <p className="text-slate-600 text-sm">{announcements.length} announcements</p>
                </button>
                <button
                  onClick={() => setActiveTab('media')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <ImageIcon size={48} className="mx-auto mb-3 text-green-600" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Media Gallery</h3>
                  <p className="text-slate-600 text-sm">{media.length} items</p>
                </button>
                <button
                  onClick={() => setActiveTab('prayer')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105 relative"
                >
                  <Heart size={48} className="mx-auto mb-3 text-red-500" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Prayer Requests</h3>
                  <p className="text-slate-600 text-sm">{prayerRequests.filter(p => p.status === 'pending').length} pending</p>
                  {prayerRequests.filter(p => p.status === 'pending').length > 0 && (
                    <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full px-2 py-1">
                      {prayerRequests.filter(p => p.status === 'pending').length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Communication & Users */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#1e3a8a' }}>
                👥 Communication & Users
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                <button
                  onClick={() => setActiveTab('sms')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <MessageSquare size={48} className="mx-auto mb-3 text-purple-600" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>SMS Groups</h3>
                  <p className="text-slate-600 text-sm">Send text messages to groups</p>
                </button>
                <button
                  onClick={() => setActiveTab('pastoral-staff')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <UsersIcon size={48} className="mx-auto mb-3 text-indigo-600" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Pastoral Staff</h3>
                  <p className="text-slate-600 text-sm">Manage callback notifications</p>
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <UserCog size={48} className="mx-auto mb-3 text-blue-600" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Manage Users</h3>
                  <p className="text-slate-600 text-sm">{allUsers.length} registered users</p>
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105"
                >
                  <BarChart3 size={48} className="mx-auto mb-3 text-teal-600" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Analytics</h3>
                  <p className="text-slate-600 text-sm">View site statistics</p>
                </button>
              </div>
            </div>

            {/* Resources & Documentation */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#1e3a8a' }}>
                📚 Resources & Documentation
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                <button
                  onClick={() => setActiveTab('docs-overview')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-br from-blue-50 to-indigo-50"
                >
                  <Book size={48} className="mx-auto mb-3 text-blue-700" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Platform Overview</h3>
                  <p className="text-slate-600 text-sm">Sales & marketing guide</p>
                </button>
                <button
                  onClick={() => setActiveTab('docs-features')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-br from-green-50 to-emerald-50"
                >
                  <FileText size={48} className="mx-auto mb-3 text-green-700" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Feature Comparison</h3>
                  <p className="text-slate-600 text-sm">Complete feature list</p>
                </button>
                <button
                  onClick={() => setActiveTab('docs-quickstart')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-br from-amber-50 to-yellow-50"
                >
                  <FileText size={48} className="mx-auto mb-3 text-amber-700" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Admin Quick Start</h3>
                  <p className="text-slate-600 text-sm">How-to guide for admins</p>
                </button>
                <button
                  onClick={() => setActiveTab('docs-releases')}
                  className="card p-6 text-center hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-br from-purple-50 to-violet-50"
                >
                  <Clock size={48} className="mx-auto mb-3 text-purple-700" />
                  <h3 className="text-lg font-bold mb-1" style={{ color: '#1e3a8a' }}>Release Notes</h3>
                  <p className="text-slate-600 text-sm">Latest updates & changes</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ==================== EDIT HOMEPAGE ==================== */}
        {activeTab === 'edit-homepage' && (
          <div className="space-y-8">
            <div className="bg-blue-50 border-l-4 border-blue-900 p-4 rounded-r-lg">
              <h2 className="text-2xl font-bold text-blue-900">🏠 Edit Homepage</h2>
              <p className="text-slate-600">Customize what visitors see on your homepage.</p>
            </div>

            {/* Hero Section */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Hero Banner</h3>
              <p className="text-slate-600 mb-4">The large banner at the top of your homepage.</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2">Hero Title</label>
                  <input type="text" value={siteSettings.hero_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, hero_title: e.target.value })} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Welcome to Our Church" />
                </div>
                <div>
                  <label className="block font-semibold mb-2">Hero Subtitle</label>
                  <input type="text" value={siteSettings.hero_subtitle || ''} onChange={(e) => setSiteSettings({ ...siteSettings, hero_subtitle: e.target.value })} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Loving God and Others" />
                </div>
                <div className="md:col-span-2">
                  <label className="block font-semibold mb-2">Hero Tagline</label>
                  <input type="text" value={siteSettings.hero_tagline || ''} onChange={(e) => setSiteSettings({ ...siteSettings, hero_tagline: e.target.value })} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Biblical Teaching • Traditional Worship" />
                </div>
                <div className="md:col-span-2">
                  <ImagePicker
                    label="Hero Background Image"
                    value={siteSettings.hero_image_url || ''}
                    onChange={(url) => setSiteSettings({ ...siteSettings, hero_image_url: url })}
                    previewHeight="h-48"
                  />
                </div>
              </div>
            </div>

            {/* Service Times */}
            <div className="card p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>Service Times Section</h3>
                <label className="flex items-center gap-2"><input type="checkbox" checked={siteSettings.show_service_times !== false} onChange={(e) => setSiteSettings({ ...siteSettings, show_service_times: e.target.checked })} className="w-5 h-5" /><span>Show</span></label>
              </div>
              <div className="mb-4">
                <label className="block font-semibold mb-2">Section Title</label>
                <input type="text" value={siteSettings.service_section_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, service_section_title: e.target.value })} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Join Us This Sunday" />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Card 1 */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-3"><h4 className="font-bold text-blue-900">Card 1</h4><label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={siteSettings.show_sunday_school !== false} onChange={(e) => setSiteSettings({ ...siteSettings, show_sunday_school: e.target.checked })} />Show</label></div>
                  <input type="text" value={siteSettings.sunday_school_label || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_label: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Title" />
                  <input type="text" value={siteSettings.sunday_school_time || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_time: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Time" />
                  <input type="text" value={siteSettings.sunday_school_location || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_location: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Location" />
                  <input type="text" value={siteSettings.sunday_school_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_description: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Description" />
                </div>
                {/* Card 2 */}
                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-3"><h4 className="font-bold text-amber-900">Card 2</h4><label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={siteSettings.show_sunday_service !== false} onChange={(e) => setSiteSettings({ ...siteSettings, show_sunday_service: e.target.checked })} />Show</label></div>
                  <input type="text" value={siteSettings.sunday_service_label || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_label: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Title" />
                  <input type="text" value={siteSettings.sunday_service_time || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_time: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Time" />
                  <input type="text" value={siteSettings.sunday_service_location || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_location: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Location" />
                  <input type="text" value={siteSettings.sunday_service_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_description: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Description" />
                </div>
                {/* Card 3 */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-3"><h4 className="font-bold text-green-900">Card 3</h4><label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={siteSettings.show_service_3 === true} onChange={(e) => setSiteSettings({ ...siteSettings, show_service_3: e.target.checked })} />Show</label></div>
                  <input type="text" value={siteSettings.service_3_label || ''} onChange={(e) => setSiteSettings({ ...siteSettings, service_3_label: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Title" />
                  <input type="text" value={siteSettings.service_3_time || ''} onChange={(e) => setSiteSettings({ ...siteSettings, service_3_time: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Time" />
                  <input type="text" value={siteSettings.service_3_location || ''} onChange={(e) => setSiteSettings({ ...siteSettings, service_3_location: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Location" />
                  <input type="text" value={siteSettings.service_3_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, service_3_description: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Description" />
                </div>
              </div>
            </div>

            {/* Mission & Quick Links */}
            <div className="card p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>Mission Section</h3>
                <label className="flex items-center gap-2"><input type="checkbox" checked={siteSettings.show_mission !== false} onChange={(e) => setSiteSettings({ ...siteSettings, show_mission: e.target.checked })} className="w-5 h-5" /><span>Show</span></label>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block font-semibold mb-2">Section Title</label><input type="text" value={siteSettings.mission_section_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, mission_section_title: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="Our Mission" /></div>
                <div><label className="block font-semibold mb-2">Scripture Reference</label><input type="text" value={siteSettings.mission_verse || ''} onChange={(e) => setSiteSettings({ ...siteSettings, mission_verse: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="Philippians 2:5" /></div>
                <div className="md:col-span-2"><label className="block font-semibold mb-2">Mission Quote</label><textarea rows={2} value={siteSettings.mission_statement || ''} onChange={(e) => setSiteSettings({ ...siteSettings, mission_statement: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="Your mission statement..." /></div>
                <div className="md:col-span-2"><label className="block font-semibold mb-2">Mission Description</label><textarea rows={2} value={siteSettings.mission_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, mission_description: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="We are a faith community..." /></div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="card p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>Quick Links Cards</h3>
                <label className="flex items-center gap-2"><input type="checkbox" checked={siteSettings.show_quick_links !== false} onChange={(e) => setSiteSettings({ ...siteSettings, show_quick_links: e.target.checked })} className="w-5 h-5" /><span>Show</span></label>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg"><h4 className="font-bold text-blue-900 mb-2">Card 1</h4><input type="text" value={siteSettings.quicklink1_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink1_title: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Title" /><textarea rows={2} value={siteSettings.quicklink1_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink1_description: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Description" /><input type="text" value={siteSettings.quicklink1_button || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink1_button: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Button Text" /></div>
                <div className="bg-amber-50 p-4 rounded-lg"><h4 className="font-bold text-amber-900 mb-2">Card 2</h4><input type="text" value={siteSettings.quicklink2_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink2_title: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Title" /><textarea rows={2} value={siteSettings.quicklink2_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink2_description: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Description" /><input type="text" value={siteSettings.quicklink2_button || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink2_button: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Button Text" /></div>
                <div className="bg-green-50 p-4 rounded-lg"><h4 className="font-bold text-green-900 mb-2">Card 3</h4><input type="text" value={siteSettings.quicklink3_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink3_title: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Title" /><textarea rows={2} value={siteSettings.quicklink3_description || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink3_description: e.target.value })} className="w-full px-3 py-2 border rounded mb-2" placeholder="Description" /><input type="text" value={siteSettings.quicklink3_button || ''} onChange={(e) => setSiteSettings({ ...siteSettings, quicklink3_button: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Button Text" /></div>
              </div>
            </div>

            <div className="text-center"><button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary text-xl px-12 py-4">{savingSettings ? 'Saving...' : '💾 Save Homepage Changes'}</button></div>
          </div>
        )}

        {/* ==================== EDIT ABOUT PAGE ==================== */}
        {activeTab === 'edit-about' && (
          <div className="space-y-8">
            <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
              <h2 className="text-2xl font-bold text-purple-900">ℹ️ Edit About Page</h2>
              <p className="text-slate-600">Customize the content on your About Us page.</p>
            </div>

            {/* Our Foundation */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Our Foundation Section</h3>
              <p className="text-slate-600 mb-4">The three content cards at the top of the About page.</p>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg"><label className="block font-bold text-blue-900 mb-2">Loving God and Others</label><textarea rows={4} value={siteSettings.about_intro || ''} onChange={(e) => setSiteSettings({ ...siteSettings, about_intro: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="At our church, we believe..." /></div>
                <div className="bg-amber-50 p-4 rounded-lg"><label className="block font-bold text-amber-900 mb-2">Biblical Teaching</label><textarea rows={4} value={siteSettings.about_biblical_teaching || ''} onChange={(e) => setSiteSettings({ ...siteSettings, about_biblical_teaching: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="We are committed to teaching..." /></div>
                <div className="bg-green-50 p-4 rounded-lg"><label className="block font-bold text-green-900 mb-2">Traditional Worship</label><textarea rows={4} value={siteSettings.about_worship || ''} onChange={(e) => setSiteSettings({ ...siteSettings, about_worship: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="We honor the rich traditions..." /></div>
              </div>
            </div>

            {/* Pastor */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Pastor Section</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div><label className="block font-semibold mb-2">Pastor Name</label><input type="text" value={siteSettings.pastor_name || ''} onChange={(e) => setSiteSettings({ ...siteSettings, pastor_name: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="Pastor Mark" /></div>
                  <div><label className="block font-semibold mb-2">Pastor Title</label><input type="text" value={siteSettings.pastor_title || ''} onChange={(e) => setSiteSettings({ ...siteSettings, pastor_title: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="Senior Pastor" /></div>
                  <div><label className="block font-semibold mb-2">Pastor Bio</label><textarea rows={4} value={siteSettings.pastor_bio || ''} onChange={(e) => setSiteSettings({ ...siteSettings, pastor_bio: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="We are blessed to have..." /></div>
                </div>
                <div>
                  <ImagePicker
                    label="Pastor Photo"
                    value={siteSettings.pastor_image_url || ''}
                    onChange={(url) => setSiteSettings({ ...siteSettings, pastor_image_url: url })}
                    previewHeight="h-48"
                  />
                </div>
              </div>
            </div>

            {/* Visit Us Section with Image */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Visit Us Section</h3>
              <p className="text-slate-600 mb-6">Customize the image and info shown in the "Visit Us" section on the About page.</p>
              
              {/* Church Building Image */}
              <div className="mb-6">
                <ImagePicker
                  label="Church Building Image"
                  value={siteSettings.visit_us_image_url || ''}
                  onChange={(url) => setSiteSettings({ ...siteSettings, visit_us_image_url: url })}
                  previewHeight="h-48"
                />
              </div>

              {/* Reference Info from other settings */}
              <div className="bg-slate-50 p-6 rounded-lg">
                <h4 className="font-semibold text-slate-700 mb-4">Address & Service Info (from General Info):</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><strong>Address:</strong> {siteSettings.address}, {siteSettings.city_state_zip}</div>
                  <div><strong>Phone:</strong> {siteSettings.phone}</div>
                  <div><strong>Sunday School:</strong> {siteSettings.sunday_school_time}</div>
                  <div><strong>Sunday Service:</strong> {siteSettings.sunday_service_time}</div>
                </div>
                <p className="text-sm text-slate-500 mt-4 italic">💡 To change address/phone/times, edit "General Info" from the dashboard.</p>
              </div>
            </div>

            <div className="text-center"><button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary text-xl px-12 py-4">{savingSettings ? 'Saving...' : '💾 Save About Page Changes'}</button></div>
          </div>
        )}

        {/* ==================== EDIT CONTACT PAGE ==================== */}
        {activeTab === 'edit-contact' && (
          <div className="space-y-8">
            <div className="bg-teal-100 border-l-4 border-teal-500 p-4 rounded-r-lg">
              <h2 className="text-2xl font-bold text-teal-900">📞 Edit Contact Page</h2>
              <p className="text-teal-700">Customize the image shown on the Contact Us page</p>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Contact Page Image</h3>
              <p className="text-slate-600 mb-6">This image appears on the right side of the Contact Us page, next to the contact information.</p>
              
              <ImagePicker
                label="Contact Page Image"
                value={siteSettings.contact_page_image_url || ''}
                onChange={(url) => setSiteSettings({ ...siteSettings, contact_page_image_url: url })}
                previewHeight="h-64"
              />
              
              {!siteSettings.contact_page_image_url && (
                <p className="text-sm text-amber-600 mt-4 italic">Currently using default placeholder image</p>
              )}
            </div>

            <div className="bg-slate-50 p-6 rounded-lg">
              <h4 className="font-semibold text-slate-700 mb-4">Contact Info (from General Info):</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div><strong>Address:</strong> {siteSettings.address}, {siteSettings.city_state_zip}</div>
                <div><strong>Phone:</strong> {siteSettings.phone}</div>
                <div><strong>Email:</strong> {siteSettings.email}</div>
                <div><strong>Service Times:</strong> {siteSettings.sunday_school_time} / {siteSettings.sunday_service_time}</div>
              </div>
              <p className="text-sm text-slate-500 mt-4 italic">💡 To change contact info, edit "General Info" from the dashboard.</p>
            </div>

            <div className="text-center"><button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary text-xl px-12 py-4">{savingSettings ? 'Saving...' : '💾 Save Contact Page Changes'}</button></div>
          </div>
        )}

        {/* ==================== EDIT GENERAL INFO ==================== */}
        {activeTab === 'edit-general' && (
          <div className="space-y-8">
            <div className="bg-slate-100 border-l-4 border-slate-500 p-4 rounded-r-lg">
              <h2 className="text-2xl font-bold text-slate-900">⚙️ General Information</h2>
              <p className="text-slate-600">Basic church info used across all pages (Contact, Footer, etc.)</p>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Church Details</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div><label className="block font-semibold mb-2">Church Name</label><input type="text" value={siteSettings.church_name || ''} onChange={(e) => setSiteSettings({ ...siteSettings, church_name: e.target.value })} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block font-semibold mb-2">Phone</label><input type="tel" value={siteSettings.phone || ''} onChange={(e) => setSiteSettings({ ...siteSettings, phone: e.target.value })} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block font-semibold mb-2">Email</label><input type="email" value={siteSettings.email || ''} onChange={(e) => setSiteSettings({ ...siteSettings, email: e.target.value })} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block font-semibold mb-2">Street Address</label><input type="text" value={siteSettings.address || ''} onChange={(e) => setSiteSettings({ ...siteSettings, address: e.target.value })} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div className="md:col-span-2"><label className="block font-semibold mb-2">City, State, ZIP</label><input type="text" value={siteSettings.city_state_zip || ''} onChange={(e) => setSiteSettings({ ...siteSettings, city_state_zip: e.target.value })} className="w-full px-4 py-3 border rounded-lg" /></div>
              </div>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Church Logo</h3>
              <p className="text-slate-600 mb-4">This logo appears in the header, footer, and homepage.</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div><label className="block font-semibold mb-2">Logo URL</label><input type="url" value={siteSettings.logo_url || ''} onChange={(e) => setSiteSettings({ ...siteSettings, logo_url: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="https://..." /></div>
                {siteSettings.logo_url && <div className="bg-white p-4 rounded-lg border"><p className="text-sm text-slate-500 mb-2">Preview:</p><img src={siteSettings.logo_url} alt="Logo" className="h-24 object-contain" /></div>}
              </div>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Footer</h3>
              <div><label className="block font-semibold mb-2">Footer Copyright Text</label><input type="text" value={siteSettings.footer_text || ''} onChange={(e) => setSiteSettings({ ...siteSettings, footer_text: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="© 2025 Church Name. All rights reserved." /></div>
            </div>

            {/* Timezone Setting */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Timezone</h3>
              <p className="text-slate-600 mb-4">Set the timezone for displaying dates and times on the website.</p>
              <div>
                <label className="block font-semibold mb-2">Website Timezone</label>
                <select
                  value={siteSettings.timezone || 'America/Los_Angeles'}
                  onChange={(e) => setSiteSettings({ ...siteSettings, timezone: e.target.value })}
                  className="w-full px-4 py-3 border border-amber-200 rounded-lg bg-white"
                  data-testid="timezone-select"
                >
                  <optgroup label="United States">
                    <option value="America/Los_Angeles">Pacific Time (PT) - Los Angeles</option>
                    <option value="America/Denver">Mountain Time (MT) - Denver</option>
                    <option value="America/Chicago">Central Time (CT) - Chicago</option>
                    <option value="America/New_York">Eastern Time (ET) - New York</option>
                    <option value="America/Anchorage">Alaska Time (AKT) - Anchorage</option>
                    <option value="Pacific/Honolulu">Hawaii Time (HT) - Honolulu</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="UTC">UTC (Coordinated Universal Time)</option>
                    <option value="Europe/London">London (GMT/BST)</option>
                    <option value="Europe/Paris">Paris (CET/CEST)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                  </optgroup>
                </select>
                <p className="text-sm text-slate-500 mt-2">Current timezone: <strong>{siteSettings.timezone || 'America/Los_Angeles'}</strong></p>
              </div>
            </div>

            {/* Admin Email Addresses */}
            <div className="card p-8">
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Admin Email Addresses</h3>
              <p className="text-slate-600 mb-6">These email addresses will receive contact form submissions and notifications from the website.</p>
              
              {/* Add New Email */}
              <div className="flex gap-4 mb-6">
                <input
                  type="email"
                  id="new-admin-email"
                  placeholder="Enter email address..."
                  className="flex-1 px-4 py-3 border border-amber-200 rounded-lg"
                  data-testid="new-admin-email-input"
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.getElementById('new-admin-email');
                    const email = input.value.trim();
                    if (email && email.includes('@')) {
                      const currentEmails = siteSettings.admin_emails || [];
                      if (!currentEmails.includes(email)) {
                        setSiteSettings({ ...siteSettings, admin_emails: [...currentEmails, email] });
                        input.value = '';
                        toast.success('Email added - remember to save!');
                      } else {
                        toast.error('Email already in list');
                      }
                    } else {
                      toast.error('Please enter a valid email');
                    }
                  }}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  data-testid="add-admin-email-btn"
                >
                  <Plus size={20} className="inline mr-1" />
                  Add Email
                </button>
              </div>

              {/* Current Emails List */}
              <div className="space-y-3">
                {(siteSettings.admin_emails || []).length === 0 ? (
                  <div className="bg-amber-50 p-4 rounded-lg text-amber-800">
                    <p>No admin email addresses configured yet. Add at least one email to receive contact form submissions.</p>
                  </div>
                ) : (
                  (siteSettings.admin_emails || []).map((email, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <span className="text-lg">{email}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newEmails = siteSettings.admin_emails.filter((_, i) => i !== index);
                          setSiteSettings({ ...siteSettings, admin_emails: newEmails });
                          toast.success('Email removed - remember to save!');
                        }}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        data-testid={`delete-admin-email-${index}`}
                      >
                        <Trash2 size={18} className="inline mr-1" />
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
              
              <p className="text-sm text-slate-500 mt-4 italic">
                💡 All emails in this list will receive contact form submissions and important notifications.
              </p>
            </div>

            <div className="text-center"><button onClick={handleSaveSettings} disabled={savingSettings} className="btn-primary text-xl px-12 py-4">{savingSettings ? 'Saving...' : '💾 Save General Info'}</button></div>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-8">
            <div className="card p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
                  {editingEvent ? '✏️ Edit Event' : 'Create New Event'}
                </h2>
                {editingEvent && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="btn-secondary text-sm"
                  >
                    <X size={18} className="inline mr-1" />
                    Cancel Edit
                  </button>
                )}
              </div>
              <form onSubmit={handleCreateEvent} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Event Title</label>
                    <input
                      type="text"
                      required
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      placeholder="e.g., Easter Service"
                      data-testid="event-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Location</label>
                    <input
                      type="text"
                      required
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      data-testid="event-location-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Description</label>
                  <textarea
                    required
                    rows={4}
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="Event details..."
                    data-testid="event-description-input"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Date</label>
                    <input
                      type="date"
                      required={!eventForm.is_recurring}
                      value={eventForm.date}
                      onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      data-testid="event-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Time</label>
                    <input
                      type="time"
                      required
                      value={eventForm.time}
                      onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      data-testid="event-time-input"
                    />
                  </div>
                </div>

                <div>
                  <ImagePicker
                    label="Event Image"
                    value={eventForm.image_url}
                    onChange={(url) => setEventForm({ ...eventForm, image_url: url })}
                    previewHeight="h-40"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_recurring"
                      checked={eventForm.is_recurring}
                      onChange={(e) => setEventForm({ ...eventForm, is_recurring: e.target.checked })}
                      className="w-6 h-6 text-blue-900 border-gray-300 rounded"
                      data-testid="event-recurring-checkbox"
                    />
                    <label htmlFor="is_recurring" className="ml-3 text-lg text-slate-700">
                      This is a recurring event
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={eventForm.is_featured}
                      onChange={(e) => setEventForm({ ...eventForm, is_featured: e.target.checked })}
                      className="w-6 h-6 text-blue-900 border-gray-300 rounded"
                      data-testid="event-featured-checkbox"
                    />
                    <label htmlFor="is_featured" className="ml-3 text-lg text-slate-700">
                      <span className="font-semibold text-amber-600">Feature this event on homepage</span> (up to 3 events will be shown)
                    </label>
                  </div>
                </div>

                {eventForm.is_recurring && (
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Recurrence Pattern</label>
                    <input
                      type="text"
                      required={eventForm.is_recurring}
                      value={eventForm.recurrence_pattern}
                      onChange={(e) => setEventForm({ ...eventForm, recurrence_pattern: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      placeholder="e.g., Every Sunday at 10:30 AM"
                      data-testid="event-pattern-input"
                    />
                  </div>
                )}

                <button type="submit" className="btn-primary" data-testid="create-event-btn">
                  {editingEvent ? (
                    <>
                      <Edit2 size={20} className="inline mr-2" />
                      Update Event
                    </>
                  ) : (
                    <>
                      <Plus size={20} className="inline mr-2" />
                      Create Event
                    </>
                  )}
                </button>
              </form>
            </div>

            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Existing Events</h2>
              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.id} className={`border rounded-lg p-6 ${editingEvent === event.id ? 'border-blue-500 border-2 bg-blue-50' : 'border-amber-200'}`} data-testid={`event-item-${event.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>{event.title}</h3>
                          {event.is_featured && (
                            <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-sm font-semibold">Featured</span>
                          )}
                          {editingEvent === event.id && (
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-semibold">Currently Editing</span>
                          )}
                        </div>
                        <p className="text-lg text-slate-600 mb-2 line-clamp-2">{event.description}</p>
                        <p className="text-lg text-slate-600">
                          {event.is_recurring ? event.recurrence_pattern : `${parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${formatTime12Hour(event.time)}`}
                        </p>
                        <p className="text-slate-500">{event.registrations} registrations</p>
                      </div>
                      <div className="flex gap-3 ml-4">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="flex items-center gap-2 bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 rounded-lg transition font-semibold"
                          data-testid={`edit-event-${event.id}`}
                        >
                          <Edit2 size={18} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="flex items-center gap-2 bg-red-100 text-red-700 hover:bg-red-200 px-4 py-2 rounded-lg transition font-semibold"
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 size={18} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <div className="space-y-8">
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Create Announcement</h2>
              <form onSubmit={handleCreateAnnouncement} className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Title</label>
                  <input
                    type="text"
                    required
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="Announcement title"
                    data-testid="announcement-title-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Content</label>
                  <textarea
                    required
                    rows={4}
                    value={announcementForm.content}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="Announcement details..."
                    data-testid="announcement-content-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Priority</label>
                  <select
                    value={announcementForm.priority}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    data-testid="announcement-priority-select"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <ImagePicker
                    label="Image (Optional)"
                    value={announcementForm.image_url}
                    onChange={(url) => setAnnouncementForm({ ...announcementForm, image_url: url })}
                    previewHeight="h-32"
                  />
                </div>
                <button type="submit" className="btn-primary" data-testid="create-announcement-btn">
                  <Plus size={20} className="inline mr-2" />
                  Create Announcement
                </button>
              </form>
            </div>

            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Existing Announcements</h2>
              <div className="space-y-4">
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="border border-amber-200 rounded-lg p-6 flex justify-between items-start" data-testid={`announcement-item-${announcement.id}`}>
                    <div>
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>{announcement.title}</h3>
                      <p className="text-lg text-slate-600 mb-2">{announcement.content}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                        announcement.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                        announcement.priority === 'high' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {announcement.priority.toUpperCase()}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteAnnouncement(announcement.id)}
                      className="text-red-600 hover:text-red-800"
                      data-testid={`delete-announcement-${announcement.id}`}
                    >
                      <Trash2 size={24} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="space-y-8">
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Add Media</h2>
              <form onSubmit={handleCreateMedia} className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Type</label>
                  <select
                    value={mediaForm.type}
                    onChange={(e) => setMediaForm({ ...mediaForm, type: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    data-testid="media-type-select"
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                
                {/* Image Upload or URL */}
                {mediaForm.type === 'image' && (
                  <div className="space-y-4">
                    <label className="block text-lg font-semibold" style={{ color: '#1e3a8a' }}>Image Source</label>
                    <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
                      <label className="block mb-2 text-slate-700 font-medium">Upload from Computer</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleMediaImageUpload}
                        disabled={uploadingMediaImage}
                        className="w-full px-4 py-3 text-lg border border-amber-300 rounded-lg bg-white"
                        data-testid="media-image-upload"
                      />
                      {uploadingMediaImage && <p className="text-amber-600 mt-2">Uploading image...</p>}
                    </div>
                    <div className="text-center text-slate-500 font-semibold">— OR —</div>
                    <div>
                      <label className="block mb-2 text-slate-700 font-medium">Paste Image URL</label>
                      <input
                        type="url"
                        value={mediaForm.url}
                        onChange={(e) => setMediaForm({ ...mediaForm, url: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                        placeholder="https://..."
                        data-testid="media-url-input"
                      />
                    </div>
                    {mediaForm.url && (
                      <div className="mt-4">
                        <p className="text-sm text-slate-600 mb-2">Preview:</p>
                        <img src={mediaForm.url} alt="Preview" className="w-full max-w-xs h-48 object-cover rounded-lg border-2 border-amber-200" />
                      </div>
                    )}
                  </div>
                )}

                {/* Video URL Only */}
                {mediaForm.type === 'video' && (
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Video URL (YouTube Embed)</label>
                    <input
                      type="url"
                      required
                      value={mediaForm.url}
                      onChange={(e) => setMediaForm({ ...mediaForm, url: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      placeholder="https://www.youtube.com/embed/..."
                      data-testid="media-url-input"
                    />
                    <p className="text-sm text-slate-600 mt-2">Use YouTube embed URL format: https://www.youtube.com/embed/VIDEO_ID</p>
                  </div>
                )}
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Title (Optional)</label>
                    <input
                      type="text"
                      value={mediaForm.title}
                      onChange={(e) => setMediaForm({ ...mediaForm, title: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      data-testid="media-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Description (Optional)</label>
                    <input
                      type="text"
                      value={mediaForm.description}
                      onChange={(e) => setMediaForm({ ...mediaForm, description: e.target.value })}
                      className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      data-testid="media-description-input"
                    />
                  </div>
                </div>
                <button type="submit" className="btn-primary" data-testid="add-media-btn" disabled={!mediaForm.url}>
                  <Plus size={20} className="inline mr-2" />
                  Add Media
                </button>
              </form>
            </div>

            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Existing Media</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {media.map((item) => (
                  <div key={item.id} className="border border-amber-200 rounded-lg overflow-hidden" data-testid={`media-item-${item.id}`}>
                    {item.type === 'image' ? (
                      <img src={item.url} alt={item.title} className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-slate-900 flex items-center justify-center">
                        <Video size={48} color="white" />
                      </div>
                    )}
                    <div className="p-4">
                      {item.title && <h3 className="text-lg font-bold mb-2" style={{ color: '#1e3a8a' }}>{item.title}</h3>}
                      <button
                        onClick={() => handleDeleteMedia(item.id)}
                        className="text-red-600 hover:text-red-800 w-full btn-secondary mt-2"
                        data-testid={`delete-media-${item.id}`}
                      >
                        <Trash2 size={20} className="inline mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SMS Groups Tab */}
        {activeTab === 'sms' && (
          <SMSDashboard />
        )}

        {/* OLD SMS TAB - KEEPING FOR REFERENCE IF NEEDED */}
        {activeTab === 'sms-old' && (
          <div className="space-y-8">
            {/* Create Group */}
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Create SMS Group</h2>
              <form onSubmit={handleCreateSmsGroup} className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Group Name</label>
                  <input
                    type="text"
                    required
                    value={smsGroupForm.name}
                    onChange={(e) => setSmsGroupForm({ ...smsGroupForm, name: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="e.g., Youth Group, Elders"
                    data-testid="sms-group-name-input"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Description</label>
                  <input
                    type="text"
                    value={smsGroupForm.description}
                    onChange={(e) => setSmsGroupForm({ ...smsGroupForm, description: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="Group description"
                    data-testid="sms-group-description-input"
                  />
                </div>
                <button type="submit" className="btn-primary" data-testid="create-sms-group-btn">
                  <Plus size={20} className="inline mr-2" />
                  Create Group
                </button>
              </form>
            </div>

            {/* Manage Groups */}
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>SMS Groups</h2>
              <div className="space-y-6">
                {smsGroups.map((group) => (
                  <div key={group.id} className="border border-amber-200 rounded-lg p-6" data-testid={`sms-group-${group.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>{group.name}</h3>
                        {group.description && <p className="text-lg text-slate-600 mb-2">{group.description}</p>}
                        <p className="text-lg text-slate-600">{group.members?.length || 0} members</p>
                      </div>
                      <button
                        onClick={() => handleDeleteSmsGroup(group.id)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`delete-sms-group-${group.id}`}
                      >
                        <Trash2 size={24} />
                      </button>
                    </div>

                    {/* Add Member Form */}
                    <div className="bg-amber-50 p-4 rounded-lg mb-4">
                      <h4 className="text-lg font-bold mb-3" style={{ color: '#1e3a8a' }}>Add Member</h4>
                      <form onSubmit={(e) => {
                        e.preventDefault();
                        setMemberForm({ ...memberForm, group_id: group.id });
                        handleAddMember(e);
                      }} className="grid md:grid-cols-3 gap-4">
                        <input
                          type="text"
                          required
                          placeholder="Name"
                          value={memberForm.group_id === group.id ? memberForm.name : ''}
                          onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value, group_id: group.id })}
                          className="px-4 py-2 text-lg border border-amber-200 rounded-lg"
                          data-testid={`member-name-${group.id}`}
                        />
                        <input
                          type="tel"
                          required
                          placeholder="+1234567890"
                          value={memberForm.group_id === group.id ? memberForm.phone : ''}
                          onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value, group_id: group.id })}
                          className="px-4 py-2 text-lg border border-amber-200 rounded-lg"
                          data-testid={`member-phone-${group.id}`}
                        />
                        <button type="submit" className="btn-secondary" data-testid={`add-member-${group.id}`}>
                          Add
                        </button>
                      </form>
                    </div>

                    {/* Members List */}
                    <div>
                      <h4 className="text-lg font-bold mb-3" style={{ color: '#1e3a8a' }}>Members</h4>
                      <div className="space-y-2">
                        {group.members?.map((member, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-lg border border-amber-200" data-testid={`member-${group.id}-${idx}`}>
                            <div>
                              <p className="text-lg font-semibold">{member.name}</p>
                              <p className="text-slate-600">{member.phone}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveMember(group.id, member.phone)}
                              className="text-red-600 hover:text-red-800"
                              data-testid={`remove-member-${group.id}-${idx}`}
                            >
                              <X size={20} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Send SMS */}
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Send Group Message</h2>
              <form onSubmit={handleSendSms} className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Select Group</label>
                  <select
                    required
                    value={smsForm.group_id}
                    onChange={(e) => setSmsForm({ ...smsForm, group_id: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    data-testid="sms-group-select"
                  >
                    <option value="">Choose a group...</option>
                    {smsGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.members?.length || 0} members)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Message</label>
                  <textarea
                    required
                    rows={4}
                    value={smsForm.message}
                    onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
                    className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                    placeholder="Type your message here..."
                    data-testid="sms-message-input"
                  />
                </div>
                <button type="submit" className="btn-primary" data-testid="send-sms-btn">
                  <Send size={20} className="inline mr-2" />
                  Send Message
                </button>
              </form>
            </div>

            {/* Message Log */}
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Message Log</h2>
              <div className="space-y-4">
                {smsMessages.map((msg) => (
                  <div key={msg.id} className="border border-amber-200 rounded-lg p-6" data-testid={`sms-log-${msg.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>{msg.group_name}</h3>
                      <span className="text-slate-600">{new Date(msg.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                    </div>
                    <p className="text-lg text-slate-700 mb-2">{msg.message}</p>
                    <p className="text-slate-600">Sent to {msg.recipients_count} recipients</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Prayer Requests Tab */}
        {activeTab === 'prayer' && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
                  <Heart size={28} className="inline mr-2" />
                  Prayer Requests
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPrayerFilter('all')}
                    className={`px-4 py-2 rounded-lg font-semibold ${prayerFilter === 'all' ? 'bg-blue-900 text-white' : 'bg-gray-100'}`}
                  >
                    All ({prayerRequests.length})
                  </button>
                  <button
                    onClick={() => setPrayerFilter('pending')}
                    className={`px-4 py-2 rounded-lg font-semibold ${prayerFilter === 'pending' ? 'bg-amber-500 text-white' : 'bg-gray-100'}`}
                  >
                    <Clock size={16} className="inline mr-1" />
                    Pending ({prayerRequests.filter(p => p.status === 'pending').length})
                  </button>
                  <button
                    onClick={() => setPrayerFilter('prayed')}
                    className={`px-4 py-2 rounded-lg font-semibold ${prayerFilter === 'prayed' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}
                  >
                    <CheckCircle size={16} className="inline mr-1" />
                    Prayed ({prayerRequests.filter(p => p.status === 'prayed').length})
                  </button>
                  <button
                    onClick={() => setPrayerFilter('archived')}
                    className={`px-4 py-2 rounded-lg font-semibold ${prayerFilter === 'archived' ? 'bg-gray-600 text-white' : 'bg-gray-100'}`}
                  >
                    <Archive size={16} className="inline mr-1" />
                    Archived
                  </button>
                </div>
              </div>

              {prayerRequests.filter(p => prayerFilter === 'all' || p.status === prayerFilter).length === 0 ? (
                <div className="text-center py-12">
                  <Heart size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-xl text-slate-600">No prayer requests found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {prayerRequests
                    .filter(p => prayerFilter === 'all' || p.status === prayerFilter)
                    .map((request) => (
                    <div key={request.id} className={`border-2 rounded-xl p-6 ${
                      request.status === 'pending' ? 'border-amber-300 bg-amber-50' :
                      request.status === 'prayed' ? 'border-green-300 bg-green-50' :
                      'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              request.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                              request.status === 'prayed' ? 'bg-green-200 text-green-800' :
                              'bg-gray-200 text-gray-800'
                            }`}>
                              {request.status === 'pending' && <Clock size={14} className="inline mr-1" />}
                              {request.status === 'prayed' && <CheckCircle size={14} className="inline mr-1" />}
                              {request.status === 'archived' && <Archive size={14} className="inline mr-1" />}
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                            <span className="text-sm text-slate-500">
                              {new Date(request.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(request.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>
                            From: {request.requester_name}
                            {request.request_for !== 'self' && (
                              <span className="text-slate-600 font-normal"> → For: {request.request_for_name || request.request_for}</span>
                            )}
                          </h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {/* Share Button */}
                          <button
                            onClick={() => {
                              setShareModal({ show: true, request });
                              setShareForm({ recipient_type: '', group_ids: [], custom_message: '' });
                            }}
                            className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-4 py-2 rounded-lg font-semibold"
                          >
                            <Send size={18} className="inline mr-1" />
                            Share
                          </button>
                          {request.status === 'pending' && (
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem('token');
                                try {
                                  await axios.put(`${API}/prayer-requests/${request.id}`, { status: 'prayed' }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Marked as prayed');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                              className="bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-lg font-semibold"
                            >
                              <CheckCircle size={18} className="inline mr-1" />
                              Mark Prayed
                            </button>
                          )}
                          {request.status !== 'archived' && (
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem('token');
                                try {
                                  await axios.put(`${API}/prayer-requests/${request.id}`, { status: 'archived' }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Archived');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg font-semibold"
                            >
                              <Archive size={18} className="inline mr-1" />
                              Archive
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (!window.confirm('Delete this prayer request?')) return;
                              const token = localStorage.getItem('token');
                              try {
                                await axios.delete(`${API}/prayer-requests/${request.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('Deleted');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to delete');
                              }
                            }}
                            className="bg-red-100 text-red-700 hover:bg-red-200 px-4 py-2 rounded-lg font-semibold"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Prayer Types */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {request.prayer_types.map((type, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {type}
                          </span>
                        ))}
                      </div>

                      {/* Details */}
                      {request.details && (
                        <div className="bg-white rounded-lg p-4 mb-4">
                          <p className="text-slate-700 whitespace-pre-wrap">{request.details}</p>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                        {request.requester_email && (
                          <span>📧 {request.requester_email}</span>
                        )}
                        {request.requester_phone && (
                          <span>📞 {request.requester_phone}</span>
                        )}
                      </div>

                      {/* Shared History */}
                      {request.shared_history && request.shared_history.length > 0 && (
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <h4 className="text-sm font-bold text-slate-600 mb-2">📤 Shared History:</h4>
                          <div className="space-y-2">
                            {request.shared_history.map((share, idx) => (
                              <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <span className="font-semibold text-purple-800">
                                      Sent to: {share.recipients.join(', ')}
                                    </span>
                                    <p className="text-slate-600">
                                      {share.sent_count} recipients • {new Date(share.shared_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(share.shared_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </p>
                                  </div>
                                  <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs">
                                    {share.recipient_type}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Share Prayer Request Modal */}
        {shareModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                  <Send size={24} className="inline mr-2" />
                  Share Prayer Request
                </h2>
                <button onClick={() => setShareModal({ show: false, request: null })} className="text-gray-500 hover:text-gray-700">
                  <X size={28} />
                </button>
              </div>

              {/* Request Preview */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600 mb-1">Prayer Request from:</p>
                <p className="font-bold text-lg" style={{ color: '#1e3a8a' }}>
                  {shareModal.request?.requester_name}
                  {shareModal.request?.request_for !== 'self' && (
                    <span className="font-normal"> → For: {shareModal.request?.request_for_name || shareModal.request?.request_for}</span>
                  )}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {shareModal.request?.prayer_types.map((type, idx) => (
                    <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">{type}</span>
                  ))}
                </div>
              </div>

              {/* Recipient Selection */}
              <div className="mb-6">
                <label className="block text-lg font-semibold mb-3" style={{ color: '#1e3a8a' }}>Send to:</label>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                    shareForm.recipient_type === 'pastor' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <input
                      type="radio"
                      name="recipient_type"
                      value="pastor"
                      checked={shareForm.recipient_type === 'pastor'}
                      onChange={(e) => setShareForm({ ...shareForm, recipient_type: e.target.value, group_ids: [] })}
                      className="w-5 h-5"
                    />
                    <div>
                      <span className="font-semibold">👨‍💼 Pastor Only</span>
                      <p className="text-sm text-slate-600">Send to Leadership Group</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                    shareForm.recipient_type === 'leadership' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <input
                      type="radio"
                      name="recipient_type"
                      value="leadership"
                      checked={shareForm.recipient_type === 'leadership'}
                      onChange={(e) => setShareForm({ ...shareForm, recipient_type: e.target.value, group_ids: [] })}
                      className="w-5 h-5"
                    />
                    <div>
                      <span className="font-semibold">👥 Leadership Team</span>
                      <p className="text-sm text-slate-600">Send to Leadership Group and Board of Directors</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                    shareForm.recipient_type === 'sms_groups' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <input
                      type="radio"
                      name="recipient_type"
                      value="sms_groups"
                      checked={shareForm.recipient_type === 'sms_groups'}
                      onChange={(e) => setShareForm({ ...shareForm, recipient_type: e.target.value })}
                      className="w-5 h-5"
                    />
                    <div>
                      <span className="font-semibold">📱 Select SMS Groups</span>
                      <p className="text-sm text-slate-600">Choose specific groups to notify</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* SMS Group Selection */}
              {shareForm.recipient_type === 'sms_groups' && (
                <div className="mb-6">
                  <label className="block text-lg font-semibold mb-3" style={{ color: '#1e3a8a' }}>Select Groups:</label>
                  <div className="grid grid-cols-2 gap-3">
                    {smsGroups.map((group) => (
                      <label
                        key={group.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                          shareForm.group_ids.includes(group.id) ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={shareForm.group_ids.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setShareForm({ ...shareForm, group_ids: [...shareForm.group_ids, group.id] });
                            } else {
                              setShareForm({ ...shareForm, group_ids: shareForm.group_ids.filter(id => id !== group.id) });
                            }
                          }}
                          className="w-5 h-5"
                        />
                        <div>
                          <span className="font-semibold">{group.name}</span>
                          <p className="text-xs text-slate-500">{group.members?.length || 0} members</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Message */}
              <div className="mb-6">
                <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                  Custom Intro Message (optional)
                </label>
                <input
                  type="text"
                  value={shareForm.custom_message}
                  onChange={(e) => setShareForm({ ...shareForm, custom_message: e.target.value })}
                  placeholder="Please join us in prayer for..."
                  className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShareModal({ show: false, request: null })}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSharePrayerRequest}
                  disabled={sharing || !shareForm.recipient_type}
                  className="btn-primary flex-1"
                >
                  {sharing ? 'Sending...' : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send Prayer Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Tab */}
        {activeTab === 'help' && (
          <div className="card p-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Admin Dashboard Guide</h2>
            <div className="space-y-6 text-lg">
              <div className="bg-amber-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>✨ New Features Added!</h3>
                <ul className="space-y-3 text-slate-700">
                  <li><strong>📸 Image Upload:</strong> You can now upload photos directly when creating events (or paste image URL)</li>
                  <li><strong>⭐ Featured Events:</strong> Check the &quot;Feature on homepage&quot; box to highlight events on the main page (up to 3 shown)</li>
                  <li><strong>📅 Calendar Sync:</strong> Visitors can download events to their personal calendar (Google Calendar, Apple Calendar, Outlook)</li>
                  <li><strong>📋 Calendar View:</strong> Events page now has both List and Calendar views</li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>How to Use Each Section:</h3>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-900 pl-4">
                    <h4 className="text-xl font-bold mb-2">Events</h4>
                    <p className="text-slate-700">Create special one-time events or recurring weekly activities. Upload photos, mark as featured to show on homepage, set date/time/location.</p>
                  </div>

                  <div className="border-l-4 border-blue-900 pl-4">
                    <h4 className="text-xl font-bold mb-2">Announcements</h4>
                    <p className="text-slate-700">Post important church updates with priority levels (Normal, High, Urgent). These appear on the site for all visitors.</p>
                  </div>

                  <div className="border-l-4 border-blue-900 pl-4">
                    <h4 className="text-xl font-bold mb-2">Media</h4>
                    <p className="text-slate-700">Add photos and videos to the gallery. For videos, paste YouTube embed URLs. Images can be uploaded or linked.</p>
                  </div>

                  <div className="border-l-4 border-blue-900 pl-4">
                    <h4 className="text-xl font-bold mb-2">SMS Groups</h4>
                    <p className="text-slate-700">Create groups (Youth, Elders, Volunteers), add members with phone numbers, send group text messages, and view message history.</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>💡 Tips for Best Results</h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Use high-quality images (at least 800x600 pixels) for events</li>
                  <li>• Feature only your most important upcoming events on the homepage</li>
                  <li>• Keep event descriptions clear and concise (2-3 sentences)</li>
                  <li>• Test SMS messages with yourself first before sending to large groups</li>
                  <li>• Update recurring events (like weekly services) as needed</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Settings Sub-Navigation */}
            <div className="card p-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSettingsSubTab('general')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${settingsSubTab === 'general' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  🏛️ General Info
                </button>
                <button
                  onClick={() => setSettingsSubTab('homepage')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${settingsSubTab === 'homepage' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  🏠 Homepage
                </button>
                <button
                  onClick={() => setSettingsSubTab('about')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${settingsSubTab === 'about' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  ℹ️ About Page
                </button>
                <button
                  onClick={() => setSettingsSubTab('footer')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${settingsSubTab === 'footer' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  📋 Footer
                </button>
              </div>
            </div>

            {/* ==================== GENERAL INFO TAB ==================== */}
            {settingsSubTab === 'general' && (
              <div className="space-y-8">
                <div className="bg-blue-50 border-l-4 border-blue-900 p-4 rounded-r-lg">
                  <h3 className="font-bold text-blue-900">General Information</h3>
                  <p className="text-slate-600">Basic church info used across all pages (Contact, Footer, Events, etc.)</p>
                </div>

                {/* Church Basic Info */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                    <Settings size={24} className="inline mr-2" />
                    Church Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Church Name</label>
                      <input
                        type="text"
                        value={siteSettings.church_name || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, church_name: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Phone</label>
                      <input
                        type="tel"
                        value={siteSettings.phone || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, phone: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Email</label>
                      <input
                        type="email"
                        value={siteSettings.email || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, email: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Street Address</label>
                      <input
                        type="text"
                        value={siteSettings.address || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, address: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>City, State, ZIP</label>
                      <input
                        type="text"
                        value={siteSettings.city_state_zip || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, city_state_zip: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Church Logo</h2>
                  <p className="text-slate-600 mb-4">This logo appears in the header, footer, and homepage.</p>
                  <div className="grid md:grid-cols-2 gap-6 items-start">
                    <div>
                      <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>Logo URL</label>
                      <input
                        type="url"
                        value={siteSettings.logo_url || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, logo_url: e.target.value })}
                        className="w-full px-4 py-3 text-lg border border-amber-200 rounded-lg"
                        placeholder="https://..."
                      />
                    </div>
                    {siteSettings.logo_url && (
                      <div className="bg-white p-4 rounded-lg border border-slate-200">
                        <p className="text-sm text-slate-500 mb-2">Preview:</p>
                        <img src={siteSettings.logo_url} alt="Logo preview" className="h-24 object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ==================== HOMEPAGE TAB ==================== */}
            {settingsSubTab === 'homepage' && (
              <div className="space-y-8">
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                  <h3 className="font-bold text-amber-900">Homepage Settings</h3>
                  <p className="text-slate-600">Customize what appears on your homepage (hero banner, services, mission, quick links)</p>
                </div>

                {/* Hero Section */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Hero Banner</h2>
                  <p className="text-slate-600 mb-6">The large banner at the top of your homepage.</p>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Hero Title</label>
                        <input
                          type="text"
                          value={siteSettings.hero_title || ''}
                          onChange={(e) => setSiteSettings({ ...siteSettings, hero_title: e.target.value })}
                          className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                          placeholder="Welcome to Our Church"
                        />
                      </div>
                      <div>
                        <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Hero Subtitle</label>
                        <input
                          type="text"
                          value={siteSettings.hero_subtitle || ''}
                          onChange={(e) => setSiteSettings({ ...siteSettings, hero_subtitle: e.target.value })}
                          className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                          placeholder="Loving God and Others"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Hero Tagline</label>
                      <input
                        type="text"
                        value={siteSettings.hero_tagline || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, hero_tagline: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="Biblical Teaching • Traditional Worship • Community"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Hero Background Image URL</label>
                      <input
                        type="url"
                        value={siteSettings.hero_image_url || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, hero_image_url: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="https://..."
                      />
                      {siteSettings.hero_image_url && (
                        <div className="mt-3 p-3 bg-slate-100 rounded-lg">
                          <p className="text-sm text-slate-500 mb-2">Preview (blue overlay will be applied):</p>
                          <img src={siteSettings.hero_image_url} alt="Hero preview" className="h-32 w-full object-cover rounded" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Times Section */}
                <div className="card p-8">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>Service Times Section</h2>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={siteSettings.show_service_times !== false}
                        onChange={(e) => setSiteSettings({ ...siteSettings, show_service_times: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="font-medium">Show on homepage</span>
                    </label>
                  </div>
                  <p className="text-slate-600 mb-6">The "Join Us This Sunday" cards showing your service times.</p>
                  
                  <div className="mb-6">
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Section Title</label>
                    <input
                      type="text"
                      value={siteSettings.service_section_title || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, service_section_title: e.target.value })}
                      className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                      placeholder="Join Us This Sunday"
                    />
                  </div>

                  <div className="space-y-6">
                    {/* Card 1 */}
                    <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-blue-900">Service Card 1</h4>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={siteSettings.show_sunday_school !== false}
                            onChange={(e) => setSiteSettings({ ...siteSettings, show_sunday_school: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Show</span>
                        </label>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Title</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_school_label || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_label: e.target.value })}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                            placeholder="Sunday School"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Time</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_school_time || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_time: e.target.value })}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                            placeholder="9:15 AM"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Location</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_school_location || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_location: e.target.value })}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                            placeholder="Fellowship Hall"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_school_description || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_school_description: e.target.value })}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                            placeholder="For all ages"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Card 2 */}
                    <div className="bg-amber-50 p-6 rounded-xl border-2 border-amber-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-amber-900">Service Card 2</h4>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={siteSettings.show_sunday_service !== false}
                            onChange={(e) => setSiteSettings({ ...siteSettings, show_sunday_service: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Show</span>
                        </label>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Title</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_service_label || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_label: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                            placeholder="Sunday Service"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Time</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_service_time || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_time: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                            placeholder="10:30 AM"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Location</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_service_location || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_location: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                            placeholder="Main Sanctuary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <input
                            type="text"
                            value={siteSettings.sunday_service_description || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, sunday_service_description: e.target.value })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                            placeholder="Traditional worship"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Card 3 (Optional) */}
                    <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-green-900">Service Card 3 (Optional)</h4>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={siteSettings.show_service_3 === true}
                            onChange={(e) => setSiteSettings({ ...siteSettings, show_service_3: e.target.checked })}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Show</span>
                        </label>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Title</label>
                          <input
                            type="text"
                            value={siteSettings.service_3_label || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, service_3_label: e.target.value })}
                            className="w-full px-3 py-2 border border-green-300 rounded-lg"
                            placeholder="Bible Study"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Time</label>
                          <input
                            type="text"
                            value={siteSettings.service_3_time || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, service_3_time: e.target.value })}
                            className="w-full px-3 py-2 border border-green-300 rounded-lg"
                            placeholder="7:00 PM"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Location</label>
                          <input
                            type="text"
                            value={siteSettings.service_3_location || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, service_3_location: e.target.value })}
                            className="w-full px-3 py-2 border border-green-300 rounded-lg"
                            placeholder="Fellowship Hall"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <input
                            type="text"
                            value={siteSettings.service_3_description || ''}
                            onChange={(e) => setSiteSettings({ ...siteSettings, service_3_description: e.target.value })}
                            className="w-full px-3 py-2 border border-green-300 rounded-lg"
                            placeholder="Weekly study"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mission Section */}
                <div className="card p-8">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>Mission Section</h2>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={siteSettings.show_mission !== false}
                        onChange={(e) => setSiteSettings({ ...siteSettings, show_mission: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="font-medium">Show on homepage</span>
                    </label>
                  </div>
                  <p className="text-slate-600 mb-6">The "Our Mission" section with your mission statement.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Section Title</label>
                      <input
                        type="text"
                        value={siteSettings.mission_section_title || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, mission_section_title: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="Our Mission"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Mission Statement (Quote)</label>
                      <textarea
                        rows={2}
                        value={siteSettings.mission_statement || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, mission_statement: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="You must have the same attitude that Christ Jesus had."
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Scripture Reference</label>
                      <input
                        type="text"
                        value={siteSettings.mission_verse || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, mission_verse: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="Philippians 2:5"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Mission Description</label>
                      <textarea
                        rows={3}
                        value={siteSettings.mission_description || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, mission_description: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="We are a faith community dedicated to..."
                      />
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="card p-8">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>Quick Links Cards</h2>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={siteSettings.show_quick_links !== false}
                        onChange={(e) => setSiteSettings({ ...siteSettings, show_quick_links: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="font-medium">Show on homepage</span>
                    </label>
                  </div>
                  <p className="text-slate-600 mb-6">The three cards at the bottom of the homepage (Events, Get Involved, Giving).</p>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-bold mb-3 text-blue-900">Card 1</h4>
                      <input
                        type="text"
                        value={siteSettings.quicklink1_title || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink1_title: e.target.value })}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg mb-2"
                        placeholder="Title"
                      />
                      <textarea
                        rows={2}
                        value={siteSettings.quicklink1_description || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink1_description: e.target.value })}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg mb-2"
                        placeholder="Description"
                      />
                      <input
                        type="text"
                        value={siteSettings.quicklink1_button || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink1_button: e.target.value })}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                        placeholder="Button text"
                      />
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-bold mb-3 text-amber-900">Card 2</h4>
                      <input
                        type="text"
                        value={siteSettings.quicklink2_title || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink2_title: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-200 rounded-lg mb-2"
                        placeholder="Title"
                      />
                      <textarea
                        rows={2}
                        value={siteSettings.quicklink2_description || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink2_description: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-200 rounded-lg mb-2"
                        placeholder="Description"
                      />
                      <input
                        type="text"
                        value={siteSettings.quicklink2_button || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink2_button: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-200 rounded-lg"
                        placeholder="Button text"
                      />
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-bold mb-3 text-green-900">Card 3</h4>
                      <input
                        type="text"
                        value={siteSettings.quicklink3_title || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink3_title: e.target.value })}
                        className="w-full px-3 py-2 border border-green-200 rounded-lg mb-2"
                        placeholder="Title"
                      />
                      <textarea
                        rows={2}
                        value={siteSettings.quicklink3_description || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink3_description: e.target.value })}
                        className="w-full px-3 py-2 border border-green-200 rounded-lg mb-2"
                        placeholder="Description"
                      />
                      <input
                        type="text"
                        value={siteSettings.quicklink3_button || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, quicklink3_button: e.target.value })}
                        className="w-full px-3 py-2 border border-green-200 rounded-lg"
                        placeholder="Button text"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== ABOUT PAGE TAB ==================== */}
            {settingsSubTab === 'about' && (
              <div className="space-y-8">
                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                  <h3 className="font-bold text-purple-900">About Page Settings</h3>
                  <p className="text-slate-600">Customize the content on your About Us page.</p>
                </div>

                {/* Our Foundation Section */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Our Foundation Section</h2>
                  <p className="text-slate-600 mb-6">The three content cards at the top of the About page.</p>
                  <div className="space-y-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-bold mb-2 text-blue-900">Loving God and Others</h4>
                      <textarea
                        rows={4}
                        value={siteSettings.about_intro || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, about_intro: e.target.value })}
                        className="w-full px-4 py-3 border border-blue-200 rounded-lg"
                        placeholder="At our church, we believe in the transformative power of love..."
                      />
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-bold mb-2 text-amber-900">Biblical Teaching</h4>
                      <textarea
                        rows={4}
                        value={siteSettings.about_biblical_teaching || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, about_biblical_teaching: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                        placeholder="We are committed to teaching the Word of God..."
                      />
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-bold mb-2 text-green-900">Traditional Worship</h4>
                      <textarea
                        rows={4}
                        value={siteSettings.about_worship || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, about_worship: e.target.value })}
                        className="w-full px-4 py-3 border border-green-200 rounded-lg"
                        placeholder="We honor the rich traditions of Christian worship..."
                      />
                    </div>
                  </div>
                </div>

                {/* Pastor Section */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Pastor Section</h2>
                  <p className="text-slate-600 mb-6">Information about your pastor that appears on the About page.</p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Pastor Name</label>
                        <input
                          type="text"
                          value={siteSettings.pastor_name || ''}
                          onChange={(e) => setSiteSettings({ ...siteSettings, pastor_name: e.target.value })}
                          className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                          placeholder="Pastor Mark"
                        />
                      </div>
                      <div>
                        <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Pastor Title</label>
                        <input
                          type="text"
                          value={siteSettings.pastor_title || ''}
                          onChange={(e) => setSiteSettings({ ...siteSettings, pastor_title: e.target.value })}
                          className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                          placeholder="Senior Pastor"
                        />
                      </div>
                      <div>
                        <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Pastor Bio</label>
                        <textarea
                          rows={4}
                          value={siteSettings.pastor_bio || ''}
                          onChange={(e) => setSiteSettings({ ...siteSettings, pastor_bio: e.target.value })}
                          className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                          placeholder="We are blessed to have Pastor Mark..."
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Pastor Photo URL</label>
                      <input
                        type="url"
                        value={siteSettings.pastor_image_url || ''}
                        onChange={(e) => setSiteSettings({ ...siteSettings, pastor_image_url: e.target.value })}
                        className="w-full px-4 py-3 border border-amber-200 rounded-lg mb-4"
                        placeholder="https://..."
                      />
                      {siteSettings.pastor_image_url && (
                        <div className="bg-slate-100 p-4 rounded-lg">
                          <p className="text-sm text-slate-500 mb-2">Preview:</p>
                          <img src={siteSettings.pastor_image_url} alt="Pastor preview" className="w-full h-48 object-cover rounded" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Visit Us Section */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>Visit Us Section</h2>
                  <p className="text-slate-600 mb-6">The location image and info shown at the bottom of the About page.</p>
                  
                  {/* Visit Us Image */}
                  <div className="mb-6">
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Church Building Image URL</label>
                    <div className="grid md:grid-cols-2 gap-6 items-start">
                      <div>
                        <input
                          type="url"
                          value={siteSettings.visit_us_image_url || ''}
                          onChange={(e) => setSiteSettings({ ...siteSettings, visit_us_image_url: e.target.value })}
                          className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                          placeholder="https://..."
                          data-testid="visit-us-image-input"
                        />
                        <p className="text-sm text-slate-500 mt-2">Enter the URL of your church building photo</p>
                      </div>
                      <div className="bg-slate-100 p-4 rounded-lg">
                        <p className="text-sm text-slate-500 mb-2">Current Image Preview:</p>
                        <img 
                          src={siteSettings.visit_us_image_url || 'https://images.unsplash.com/photo-1694878276633-dc6bcba7ee33?crop=entropy&cs=srgb&fm=jpg&q=85'} 
                          alt="Visit Us preview" 
                          className="w-full h-40 object-cover rounded"
                          data-testid="visit-us-image-preview"
                        />
                        {!siteSettings.visit_us_image_url && (
                          <p className="text-xs text-amber-600 mt-2 italic">Using default placeholder image</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Reference Info */}
                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-slate-700 mb-4">Other Visit Us Info (from other settings):</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-2">Address (from General Info)</h4>
                        <p className="text-lg">{siteSettings.address || 'Not set'}</p>
                        <p className="text-lg">{siteSettings.city_state_zip || 'Not set'}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-2">Phone (from General Info)</h4>
                        <p className="text-lg">{siteSettings.phone || 'Not set'}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-2">Service Times (from Homepage)</h4>
                        <p className="text-lg">Sunday School: {siteSettings.sunday_school_time || 'Not set'}</p>
                        <p className="text-lg">Sunday Service: {siteSettings.sunday_service_time || 'Not set'}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mt-4 italic">
                      💡 To update address/phone/service times, go to the "General Info" or "Homepage" tabs.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== FOOTER TAB ==================== */}
            {settingsSubTab === 'footer' && (
              <div className="space-y-8">
                <div className="bg-slate-100 border-l-4 border-slate-500 p-4 rounded-r-lg">
                  <h3 className="font-bold text-slate-900">Footer Settings</h3>
                  <p className="text-slate-600">Customize the footer that appears at the bottom of every page.</p>
                </div>

                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>Footer Content</h2>
                  <div className="bg-slate-50 p-6 rounded-lg mb-6">
                    <p className="text-slate-600 mb-4">The footer automatically displays:</p>
                    <ul className="list-disc list-inside text-slate-700 space-y-2">
                      <li><strong>Logo</strong> - From General Info</li>
                      <li><strong>Church Name</strong> - From General Info</li>
                      <li><strong>Contact Info</strong> (Address, Phone) - From General Info</li>
                      <li><strong>Service Times</strong> - From Homepage settings</li>
                    </ul>
                  </div>
                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Footer Copyright Text</label>
                    <input
                      type="text"
                      value={siteSettings.footer_text || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, footer_text: e.target.value })}
                      className="w-full px-4 py-3 border border-amber-200 rounded-lg"
                      placeholder="© 2025 Church Name. All rights reserved."
                    />
                  </div>
                </div>

                {/* Footer Preview */}
                <div className="card p-8">
                  <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e3a8a' }}>Footer Preview</h2>
                  <div className="bg-blue-900 text-white p-8 rounded-lg">
                    <div className="grid md:grid-cols-3 gap-6 text-center md:text-left">
                      <div>
                        {siteSettings.logo_url && (
                          <div className="bg-white rounded-lg p-3 inline-block mb-3">
                            <img src={siteSettings.logo_url} alt="Logo" className="h-16 object-contain" />
                          </div>
                        )}
                        <h4 className="font-bold text-lg">{siteSettings.church_name || 'Church Name'}</h4>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Contact</h4>
                        <p>{siteSettings.address || 'Address'}</p>
                        <p>{siteSettings.city_state_zip || 'City, State ZIP'}</p>
                        <p>{siteSettings.phone || 'Phone'}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Service Times</h4>
                        <p>Sunday School: {siteSettings.sunday_school_time || '9:15 AM'}</p>
                        <p>Sunday Service: {siteSettings.sunday_service_time || '10:30 AM'}</p>
                      </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-blue-800 text-center">
                      <p>{siteSettings.footer_text || '© 2025 Church Name. All rights reserved.'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button - Always visible */}
            <div className="flex justify-center pt-6">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="btn-primary text-xl px-12 py-4"
                data-testid="save-settings-btn"
              >
                {savingSettings ? 'Saving...' : '💾 Save All Settings'}
              </button>
            </div>
          </div>
        )}


                {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-8">
            <div className="card p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                <UserCog size={28} className="inline mr-3" />
                User Management
              </h2>
              <p className="text-slate-600 mb-6">Manage all registered users, change roles, and view activity.</p>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-amber-200">
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#1e3a8a' }}>User</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#1e3a8a' }}>Email</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#1e3a8a' }}>Role</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#1e3a8a' }}>Last Activity</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: '#1e3a8a' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u) => (
                      <tr key={u.id} className="border-b border-amber-100 hover:bg-amber-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-900 font-semibold">
                                {u.first_name?.[0]}{u.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold">{u.first_name} {u.last_name}</p>
                              <p className="text-sm text-slate-500">{u.phone || 'No phone'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{u.email}</td>
                        <td className="py-3 px-4">
                          <select
                            value={u.role || (u.is_admin ? 'admin' : 'member')}
                            onChange={(e) => handleChangeRole(u.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              (u.role === 'admin' || u.is_admin) 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-green-100 text-green-800'
                            }`}
                            disabled={u.email === user?.email}
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div>
                            <p className="text-slate-700">
                              {(u.last_activity || u.last_login)
                                ? new Date(u.last_activity || u.last_login).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                                : 'Never'
                              }
                            </p>
                            {u.last_activity_type && (
                              <p className="text-xs text-slate-500 capitalize">
                                {u.last_activity_type.replace(/_/g, ' ')}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditUser(u)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit User"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleViewLoginHistory(u.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="View Login History"
                            >
                              <Activity size={18} />
                            </button>
                            {u.email !== user?.email && (
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Delete User"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {allUsers.length === 0 && (
                <p className="text-center text-slate-500 py-8">No users registered yet.</p>
              )}
            </div>

            {/* Edit User Modal */}
            {selectedUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>Edit User</h3>
                    <button onClick={() => setSelectedUser(null)} className="text-slate-400 hover:text-slate-600">
                      <X size={24} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block font-semibold mb-1" style={{ color: '#1e3a8a' }}>First Name</label>
                      <input
                        type="text"
                        value={userEditForm.first_name}
                        onChange={(e) => setUserEditForm({ ...userEditForm, first_name: e.target.value })}
                        className="w-full px-4 py-2 border border-amber-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1" style={{ color: '#1e3a8a' }}>Last Name</label>
                      <input
                        type="text"
                        value={userEditForm.last_name}
                        onChange={(e) => setUserEditForm({ ...userEditForm, last_name: e.target.value })}
                        className="w-full px-4 py-2 border border-amber-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1" style={{ color: '#1e3a8a' }}>Phone</label>
                      <input
                        type="tel"
                        value={userEditForm.phone}
                        onChange={(e) => setUserEditForm({ ...userEditForm, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-amber-200 rounded-lg"
                      />
                    </div>

                    {/* Notification Preferences */}
                    <div className="pt-4 border-t border-slate-200">
                      <label className="block font-semibold mb-3" style={{ color: '#1e3a8a' }}>
                        <Bell size={18} className="inline mr-2" />
                        Notification Preferences
                      </label>
                      
                      {/* Announcements */}
                      <div className="bg-amber-50 p-3 rounded-lg mb-3">
                        <h5 className="font-semibold text-amber-900 text-sm mb-2">📢 New Announcements</h5>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={userEditForm.notification_preferences?.announcements_email || false}
                              onChange={(e) => setUserEditForm({
                                ...userEditForm,
                                notification_preferences: {
                                  ...userEditForm.notification_preferences,
                                  announcements_email: e.target.checked
                                }
                              })}
                              className="w-4 h-4 text-amber-700 rounded"
                            />
                            <span className="text-sm">Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={userEditForm.notification_preferences?.announcements_text || false}
                              onChange={(e) => setUserEditForm({
                                ...userEditForm,
                                notification_preferences: {
                                  ...userEditForm.notification_preferences,
                                  announcements_text: e.target.checked
                                }
                              })}
                              className="w-4 h-4 text-amber-700 rounded"
                            />
                            <span className="text-sm">Text</span>
                          </label>
                        </div>
                      </div>

                      {/* Event Reminders */}
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <h5 className="font-semibold text-blue-900 text-sm mb-1">📅 Event Reminders</h5>
                        <p className="text-xs text-blue-700 mb-2">Day-before reminders with event name, time & location</p>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={userEditForm.notification_preferences?.event_reminders_email || false}
                              onChange={(e) => setUserEditForm({
                                ...userEditForm,
                                notification_preferences: {
                                  ...userEditForm.notification_preferences,
                                  event_reminders_email: e.target.checked
                                }
                              })}
                              className="w-4 h-4 text-blue-900 rounded"
                            />
                            <span className="text-sm">Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={userEditForm.notification_preferences?.event_reminders_text || false}
                              onChange={(e) => setUserEditForm({
                                ...userEditForm,
                                notification_preferences: {
                                  ...userEditForm.notification_preferences,
                                  event_reminders_text: e.target.checked
                                }
                              })}
                              className="w-4 h-4 text-blue-900 rounded"
                            />
                            <span className="text-sm">Text</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={handleSaveUser}
                        disabled={savingUser}
                        className="btn-primary flex-1"
                      >
                        {savingUser ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={() => setSelectedUser(null)}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Login History Modal */}
            {showLoginHistory && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                      <Activity size={24} className="inline mr-2" />
                      Login History
                    </h3>
                    <button onClick={() => setShowLoginHistory(null)} className="text-slate-400 hover:text-slate-600">
                      <X size={24} />
                    </button>
                  </div>

                  {loginHistory.length > 0 ? (
                    <div className="space-y-2">
                      {loginHistory.slice(0, 20).map((entry, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-semibold text-slate-700">
                              {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
                              })}
                            </p>
                            <p className="text-sm text-slate-500">
                              {new Date(entry.timestamp).toLocaleTimeString('en-US', { 
                                hour: 'numeric', minute: '2-digit', hour12: true 
                              })}
                            </p>
                          </div>
                          {entry.ip_address && (
                            <span className="text-sm text-slate-400">IP: {entry.ip_address}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No login history available.</p>
                  )}

                  <button
                    onClick={() => setShowLoginHistory(null)}
                    className="btn-secondary w-full mt-6"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && adminStats && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card p-6 text-center">
                <UsersIcon size={40} className="mx-auto mb-3 text-blue-600" />
                <h3 className="text-4xl font-bold text-blue-900">{adminStats.users.total}</h3>
                <p className="text-slate-600">Total Users</p>
                <div className="flex justify-center gap-4 mt-2 text-sm">
                  <span className="text-purple-600">{adminStats.users.admins} admins</span>
                  <span className="text-green-600">{adminStats.users.members} members</span>
                </div>
              </div>

              <div className="card p-6 text-center">
                <Activity size={40} className="mx-auto mb-3 text-green-600" />
                <h3 className="text-4xl font-bold text-green-700">{adminStats.logins.last_24h}</h3>
                <p className="text-slate-600">Logins (24h)</p>
                <div className="flex justify-center gap-4 mt-2 text-sm">
                  <span className="text-slate-500">{adminStats.logins.last_7d} this week</span>
                </div>
              </div>

              <div className="card p-6 text-center">
                <TrendingUp size={40} className="mx-auto mb-3 text-amber-500" />
                <h3 className="text-4xl font-bold text-amber-600">{adminStats.registrations.last_7d}</h3>
                <p className="text-slate-600">New Users (7d)</p>
                <div className="flex justify-center gap-4 mt-2 text-sm">
                  <span className="text-slate-500">{adminStats.registrations.last_30d} this month</span>
                </div>
              </div>

              <div className="card p-6 text-center">
                <Heart size={40} className="mx-auto mb-3 text-red-500" />
                <h3 className="text-4xl font-bold text-red-600">{adminStats.autogiving.percentage}%</h3>
                <p className="text-slate-600">AutoGiving</p>
                <div className="flex justify-center gap-4 mt-2 text-sm">
                  <span className="text-slate-500">{adminStats.autogiving.users} users enrolled</span>
                </div>
              </div>
            </div>

            {/* Notification Stats */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                <Bell size={24} className="inline mr-2" />
                Notification Preferences
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                      <UsersIcon size={24} className="text-blue-700" />
                    </div>
                    <div>
                      <h4 className="text-2xl font-bold text-blue-900">{adminStats.notifications.email_enabled}</h4>
                      <p className="text-blue-700">Users opted in for Email</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                      <MessageSquare size={24} className="text-green-700" />
                    </div>
                    <div>
                      <h4 className="text-2xl font-bold text-green-900">{adminStats.notifications.text_enabled}</h4>
                      <p className="text-green-700">Users opted in for Text</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Users */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                <UsersIcon size={24} className="inline mr-2" />
                Recent Registrations
              </h2>
              <div className="space-y-3">
                {adminStats.recent_users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-900 font-semibold text-sm">
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold">{u.first_name} {u.last_name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    <span className="text-sm text-slate-400">
                      {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
                {adminStats.recent_users.length === 0 && (
                  <p className="text-center text-slate-500 py-4">No recent registrations.</p>
                )}
              </div>
            </div>

            {/* Login Activity Summary */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                <BarChart3 size={24} className="inline mr-2" />
                Login Activity Summary
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 border-2 border-blue-200 rounded-xl">
                  <h4 className="text-3xl font-bold text-blue-900">{adminStats.logins.last_24h}</h4>
                  <p className="text-slate-600">Last 24 Hours</p>
                </div>
                <div className="text-center p-4 border-2 border-green-200 rounded-xl">
                  <h4 className="text-3xl font-bold text-green-700">{adminStats.logins.last_7d}</h4>
                  <p className="text-slate-600">Last 7 Days</p>
                </div>
                <div className="text-center p-4 border-2 border-amber-200 rounded-xl">
                  <h4 className="text-3xl font-bold text-amber-600">{adminStats.logins.last_30d}</h4>
                  <p className="text-slate-600">Last 30 Days</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== DOCUMENTATION TABS ==================== */}
        {activeTab === 'docs-overview' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="bg-blue-50 border-l-4 border-blue-700 p-4 rounded-r-lg flex-1">
                <h2 className="text-2xl font-bold text-blue-900">📖 FaithConnect Platform Overview</h2>
                <p className="text-slate-600">Complete sales and marketing guide for the platform</p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    const element = document.getElementById('doc-overview-content');
                    const opt = {
                      margin: 0.5,
                      filename: 'FaithConnect_Platform_Overview.pdf',
                      image: { type: 'jpeg', quality: 0.98 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
                    };
                    html2pdf().set(opt).from(element).save();
                    toast.success('Downloading PDF...');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Download size={18} /> Download PDF
                </button>
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
                >
                  ← Back
                </button>
              </div>
            </div>
            
            <div id="doc-overview-content" className="card p-8 prose prose-lg max-w-none bg-white">
              <h1 className="text-3xl font-bold text-blue-900 border-b pb-4">FaithConnect Church Management Platform</h1>
              <h2 className="text-xl text-blue-800 mt-6">Complete Digital Solution for Modern Churches</h2>
              <p className="text-slate-700">FaithConnect is a comprehensive, all-in-one church management platform designed to help congregations connect, communicate, and grow. Built with modern technology and a user-friendly interface, it empowers church administrators to manage their entire digital presence from a single dashboard.</p>
              
              <h2 className="text-2xl font-bold text-blue-900 mt-8 border-b pb-2">Why Churches Choose FaithConnect</h2>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>All-in-One Solution:</strong> Website, SMS messaging, email notifications, event management, and more</li>
                <li><strong>No Technical Expertise Required:</strong> Intuitive admin dashboard for easy content management</li>
                <li><strong>Mobile-Friendly:</strong> Responsive design works beautifully on all devices</li>
                <li><strong>TCPA Compliant:</strong> Built-in compliance for text messaging regulations</li>
                <li><strong>Secure & Reliable:</strong> Enterprise-grade security with MongoDB database</li>
                <li><strong>Customizable:</strong> Tailor every aspect to match your church's brand and needs</li>
              </ul>
              
              <h2 className="text-2xl font-bold text-blue-900 mt-8 border-b pb-2">Core Features</h2>
              
              <h3 className="text-xl font-semibold text-blue-800 mt-6">1. Beautiful Public Website</h3>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-bold">Homepage</h4>
                  <p className="text-sm text-slate-600">Hero banner, service times, mission, featured events, announcements</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-bold">About Page</h4>
                  <p className="text-sm text-slate-600">Church story, leadership profiles, ministries, directions</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-bold">Events & Media</h4>
                  <p className="text-sm text-slate-600">Event listings, photo galleries, YouTube integration</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-bold">Prayer & Contact</h4>
                  <p className="text-sm text-slate-600">Online prayer requests, smart contact form</p>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-blue-800 mt-6">2. Powerful Admin Dashboard</h3>
              <p className="text-slate-700">Access all management features from one centralized, card-based dashboard including content editing, event management, announcements, and user management.</p>
              
              <h3 className="text-xl font-semibold text-blue-800 mt-6">3. SMS Communication Suite</h3>
              <ul className="list-disc pl-6 space-y-1 text-slate-700">
                <li>Create unlimited messaging groups</li>
                <li>Schedule messages for specific dates and times</li>
                <li>Automatic TCPA-compliant headers and footers</li>
                <li>Bulk CSV import with smart column mapping</li>
                <li>Complete message history and search</li>
                <li>Opt-out management built-in</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-blue-800 mt-6">4. Integrations</h3>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">📺</div>
                  <h4 className="font-bold">YouTube</h4>
                  <p className="text-sm text-slate-600">Auto-display latest sermon</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">📱</div>
                  <h4 className="font-bold">Facebook</h4>
                  <p className="text-sm text-slate-600">Embedded page feed</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">✉️</div>
                  <h4 className="font-bold">Email</h4>
                  <p className="text-sm text-slate-600">SendGrid integration</p>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl text-center">
                <h3 className="text-xl font-bold text-blue-900">Ready to Transform Your Church's Digital Presence?</h3>
                <p className="text-slate-700 mt-2">Contact us today for a demo!</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'docs-features' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="bg-green-50 border-l-4 border-green-700 p-4 rounded-r-lg flex-1">
                <h2 className="text-2xl font-bold text-green-900">📋 Feature Comparison</h2>
                <p className="text-slate-600">Complete feature list and comparison chart</p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    const element = document.getElementById('doc-features-content');
                    const opt = {
                      margin: 0.5,
                      filename: 'FaithConnect_Feature_Comparison.pdf',
                      image: { type: 'jpeg', quality: 0.98 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
                    };
                    html2pdf().set(opt).from(element).save();
                    toast.success('Downloading PDF...');
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Download size={18} /> Download PDF
                </button>
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
                >
                  ← Back
                </button>
              </div>
            </div>
            
            <div id="doc-features-content" className="card p-8 bg-white">
              <h3 className="text-xl font-bold text-green-900 mb-6">At-a-Glance Feature List</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="p-3 border font-bold">Feature</th>
                      <th className="p-3 border font-bold text-center">Included</th>
                      <th className="p-3 border font-bold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-blue-50"><td colSpan="3" className="p-2 font-bold text-blue-900">WEBSITE</td></tr>
                    <tr><td className="p-3 border">Responsive Design</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Looks great on desktop, tablet, and mobile</td></tr>
                    <tr><td className="p-3 border">Custom Homepage</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Hero banner, service times, mission, events</td></tr>
                    <tr><td className="p-3 border">About Page</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Church story, leadership, ministries</td></tr>
                    <tr><td className="p-3 border">Events & Media</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Event listings, galleries, YouTube</td></tr>
                    <tr><td className="p-3 border">Prayer Requests</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Online submission with privacy options</td></tr>
                    <tr><td className="p-3 border">Legal Pages</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Privacy Policy & Terms of Service</td></tr>
                    
                    <tr className="bg-purple-50"><td colSpan="3" className="p-2 font-bold text-purple-900">ADMIN DASHBOARD</td></tr>
                    <tr><td className="p-3 border">Content Editor</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Edit all pages from one place</td></tr>
                    <tr><td className="p-3 border">Image Library</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Upload and manage all media</td></tr>
                    <tr><td className="p-3 border">Event Management</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Create, edit, delete, feature events</td></tr>
                    <tr><td className="p-3 border">User Management</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">View members, assign roles</td></tr>
                    
                    <tr className="bg-amber-50"><td colSpan="3" className="p-2 font-bold text-amber-900">SMS MESSAGING</td></tr>
                    <tr><td className="p-3 border">Group Messaging</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Create unlimited groups</td></tr>
                    <tr><td className="p-3 border">Message Scheduling</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Set exact date and time</td></tr>
                    <tr><td className="p-3 border">Auto Header/Footer</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Professional church branding</td></tr>
                    <tr><td className="p-3 border">CSV Import</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Bulk upload with column mapping</td></tr>
                    <tr><td className="p-3 border">TCPA Compliance</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">STOP/HELP handling, opt-out management</td></tr>
                    
                    <tr className="bg-red-50"><td colSpan="3" className="p-2 font-bold text-red-900">INTEGRATIONS</td></tr>
                    <tr><td className="p-3 border">YouTube</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Auto-display latest sermon</td></tr>
                    <tr><td className="p-3 border">Facebook</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Embedded page feed</td></tr>
                    <tr><td className="p-3 border">SendGrid Email</td><td className="p-3 border text-center text-green-600 text-xl">✓</td><td className="p-3 border text-sm">Reliable email delivery</td></tr>
                    <tr><td className="p-3 border">Google Calendar</td><td className="p-3 border text-center text-amber-600">🔜</td><td className="p-3 border text-sm">Coming soon</td></tr>
                    <tr><td className="p-3 border">PayPal/Venmo</td><td className="p-3 border text-center text-amber-600">🔜</td><td className="p-3 border text-sm">Coming soon</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'docs-quickstart' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="bg-amber-50 border-l-4 border-amber-700 p-4 rounded-r-lg flex-1">
                <h2 className="text-2xl font-bold text-amber-900">🚀 Admin Quick Start Guide</h2>
                <p className="text-slate-600">Step-by-step guide for using the admin dashboard</p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    const element = document.getElementById('doc-quickstart-content');
                    const opt = {
                      margin: 0.5,
                      filename: 'FaithConnect_Admin_Quick_Start.pdf',
                      image: { type: 'jpeg', quality: 0.98 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
                    };
                    html2pdf().set(opt).from(element).save();
                    toast.success('Downloading PDF...');
                  }}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                >
                  <Download size={18} /> Download PDF
                </button>
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
                >
                  ← Back
                </button>
              </div>
            </div>
            
            <div id="doc-quickstart-content" className="card p-8 prose prose-lg max-w-none bg-white">
              <h3 className="text-xl font-bold text-amber-900">Getting Started</h3>
              
              <div className="bg-amber-50 p-4 rounded-lg my-4">
                <h4 className="font-bold">Dashboard Overview</h4>
                <p className="text-sm">After logging in, you'll see the Admin Dashboard with organized cards for each feature grouped into sections: Edit Page Content, Manage Content, Communication & Users, and Resources.</p>
              </div>
              
              <h3 className="text-xl font-bold text-amber-900 mt-6">Common Tasks</h3>
              
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold">Editing Homepage Content</h4>
                  <ol className="list-decimal pl-6 text-sm space-y-1">
                    <li>Click the <strong>Homepage</strong> card</li>
                    <li>Modify hero banner, service times, or mission statement</li>
                    <li>Click <strong>Save Changes</strong></li>
                  </ol>
                </div>
                
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-bold">Adding an Event</h4>
                  <ol className="list-decimal pl-6 text-sm space-y-1">
                    <li>Click the <strong>Events</strong> card</li>
                    <li>Click <strong>Add Event</strong></li>
                    <li>Fill in event details (name, date, description)</li>
                    <li>Check "Featured" to highlight on homepage</li>
                    <li>Click <strong>Create Event</strong></li>
                  </ol>
                </div>
                
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold">Sending an SMS Message</h4>
                  <ol className="list-decimal pl-6 text-sm space-y-1">
                    <li>Click the <strong>SMS Groups</strong> card</li>
                    <li>Go to <strong>Send Message</strong> tab</li>
                    <li>Select groups to message</li>
                    <li>Type your message (320 chars max)</li>
                    <li>Set date/time to schedule, or leave blank for immediate</li>
                    <li>Click <strong>Send Now</strong> or <strong>Schedule Message</strong></li>
                  </ol>
                </div>
                
                <div className="border-l-4 border-amber-500 pl-4">
                  <h4 className="font-bold">Importing Contacts via CSV</h4>
                  <ol className="list-decimal pl-6 text-sm space-y-1">
                    <li>Go to SMS Groups or Contacts section</li>
                    <li>Click <strong>Upload CSV</strong></li>
                    <li>Map columns: First Name, Last Name, Phone 1, Phone 2, Email, Notes</li>
                    <li>Preview the data</li>
                    <li>Click <strong>Import</strong></li>
                  </ol>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-amber-900 mt-6">Tips & Best Practices</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>SMS:</strong> Keep messages under 160 characters for best delivery</li>
                <li><strong>Events:</strong> Mark important events as "Featured"</li>
                <li><strong>Media:</strong> Use high-quality, optimized images</li>
                <li><strong>Content:</strong> Update service times seasonally</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'docs-releases' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="bg-purple-50 border-l-4 border-purple-700 p-4 rounded-r-lg flex-1">
                <h2 className="text-2xl font-bold text-purple-900">📝 Release Notes</h2>
                <p className="text-slate-600">Latest updates and feature additions</p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    const element = document.getElementById('doc-releases-content');
                    const opt = {
                      margin: 0.5,
                      filename: 'FaithConnect_Release_Notes.pdf',
                      image: { type: 'jpeg', quality: 0.98 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
                    };
                    html2pdf().set(opt).from(element).save();
                    toast.success('Downloading PDF...');
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Download size={18} /> Download PDF
                </button>
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
                >
                  ← Back
                </button>
              </div>
            </div>
            
            <div id="doc-releases-content" className="card p-8 bg-white">
              <div className="space-y-8">
                <div className="border-l-4 border-blue-500 pl-6">
                  <h3 className="text-xl font-bold text-blue-900">April 7, 2026 - SMS Scheduling Enhancement</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">NEW</span>
                      <div>
                        <h4 className="font-semibold">Specific Time Scheduling</h4>
                        <p className="text-sm text-slate-600">Separate Date and Time input fields for scheduling SMS messages with timezone display</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">NEW</span>
                      <div>
                        <h4 className="font-semibold">Automatic SMS Branding</h4>
                        <p className="text-sm text-slate-600">All messages now include church header and "Reply STOP to unsubscribe" footer</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">FIX</span>
                      <div>
                        <h4 className="font-semibold">CSV Import Duplicate Fix</h4>
                        <p className="text-sm text-slate-600">Fixed issue where CSV uploads were creating duplicate entries</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-l-4 border-green-500 pl-6">
                  <h3 className="text-xl font-bold text-green-900">April 1, 2026 - CSV Import Enhancement</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">NEW</span>
                      <div>
                        <h4 className="font-semibold">Enhanced Column Mapping</h4>
                        <p className="text-sm text-slate-600">Support for First Name, Last Name, Phone 1, Phone 2, Email, and Notes columns</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">IMPROVED</span>
                      <div>
                        <h4 className="font-semibold">Performance Optimizations</h4>
                        <p className="text-sm text-slate-600">Faster page loads with caching and parallel API calls</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-l-4 border-amber-500 pl-6">
                  <h3 className="text-xl font-bold text-amber-900">March 2026 - Bulk Management & Vonage</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">NEW</span>
                      <div>
                        <h4 className="font-semibold">Vonage SMS Integration</h4>
                        <p className="text-sm text-slate-600">Migrated to Vonage with TCPA compliance, STOP/HELP handling</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">NEW</span>
                      <div>
                        <h4 className="font-semibold">Bulk Delete & Undo Import</h4>
                        <p className="text-sm text-slate-600">Select multiple members to delete, undo recent CSV imports</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">NEW</span>
                      <div>
                        <h4 className="font-semibold">Message History Management</h4>
                        <p className="text-sm text-slate-600">Filter by month, search, hide/unhide, delete messages</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-l-4 border-slate-400 pl-6">
                  <h3 className="text-xl font-bold text-slate-700">Coming Soon</h3>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <h4 className="font-semibold">Google Calendar Integration</h4>
                      <p className="text-sm text-slate-600">Sync church calendar</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <h4 className="font-semibold">PayPal/Venmo Donations</h4>
                      <p className="text-sm text-slate-600">Online giving</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <h4 className="font-semibold">Social Media Auto-Post</h4>
                      <p className="text-sm text-slate-600">Auto-post events to Facebook</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <h4 className="font-semibold">Advanced Analytics</h4>
                      <p className="text-sm text-slate-600">Detailed usage metrics</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
